import { cur } from '@/lib/currency';
import { askCancel } from '@/lib/cancelReason';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { SalaryChangesPanel } from '@/components/SalaryChangesPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, DollarSign, AlertTriangle, Eye, Calendar, FileText, Briefcase, UserX, UserCheck, Calculator, Mail, Send, Download, ClipboardCheck, CheckCircle2, KeyRound, Copy, RefreshCw, Users, Search, MoreHorizontal, Sparkles, Banknote, History, CalendarDays, ArrowLeftRight } from 'lucide-react';
import PageTour from '@/components/PageTour';
import ExportButton from '@/components/ExportButton';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { ExportButtons } from '@/components/ExportButtons';
import { BranchFilter } from '@/components/BranchFilter';
import BankAccountsEditor from '@/components/BankAccountsEditor';
import { BulkSalaryPayment } from '@/components/BulkSalaryPayment';
import { SalaryStatusDialog } from '@/components/SalaryStatusDialog';
import { SalaryDeductionCard } from '@/components/SalaryDeductionCard';
import BankPaymentDialog from '@/components/BankPaymentDialog';
import { useBranchStore, useAuthStore } from '@/stores';
import { handleExport } from '@/lib/exportCsv';
import { localToday, toLocalYmd } from '@/lib/dateRange';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const PAYMENT_TYPES = [
  { value: 'salary', label: 'Salary', color: 'bg-success/20 text-success' },
  { value: 'bonus', label: 'Bonus', color: 'bg-primary/20 text-primary' },
  { value: 'old_balance', label: 'Old Balance Payment', color: 'bg-info/20 text-info' },
  { value: 'advance', label: 'Advance / Loan', color: 'bg-info/20 text-info' },
  { value: 'loan_repayment', label: 'Loan Repayment', color: 'bg-primary/20 text-primary' },
  { value: 'overtime', label: 'Overtime', color: 'bg-primary/20 text-primary' },
  { value: 'tickets', label: 'Tickets', color: 'bg-warning/20 text-warning' },
  { value: 'id_card', label: 'ID Card', color: 'bg-error/20 text-error' },
];
const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'unpaid', label: 'Unpaid Leave' },
  { value: 'other', label: 'Other' },
];

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const authUser = useAuthStore((s) => s.user);
  const canRepair = ['admin', 'owner', 'super_admin'].includes((authUser?.role || '').toLowerCase());
  const branchName = (id) => branches.find((b) => b.id === id)?.name || '—';

  const repairSalaryHistory = async () => {
    try {
      const r = await api.post('/salary-payments/backfill-branches');
      toast.success(`Scanned ${r.data?.scanned ?? 0}, Fixed ${r.data?.updated ?? 0}`);
    } catch (e) {
      toast.error(`Repair failed (HTTP ${e.response?.status ?? 'network'}): ${e.response?.data?.detail || e.message}`);
    }
  };

  const exportAllSalaries = async () => {
    try {
      const { data } = await api.get('/salary-payments');
      const rows = (data || []).map((p) => ({
        name: p.employee_name || '—',
        month: p.period || (p.date ? String(p.date).slice(0, 7) : '—'),
        paid_salary: Number(p.amount) || 0,
        home_branch: branchName(p.branch_id),
      }));
      handleExport(rows, 'all_salaries', [
        { key: 'name', label: 'Name' },
        { key: 'month', label: 'Month' },
        { key: 'paid_salary', label: 'Paid Salary' },
        { key: 'home_branch', label: 'Home Branch' },
      ], 'salary payments', 'paid_salary');
    } catch (e) {
      toast.error(`Export failed (HTTP ${e.response?.status ?? 'network'}): ${e.response?.data?.detail || e.message}`);
    }
  };

  const [jobTitles, setJobTitles] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [pendingSummary, setPendingSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  // "Pay from Bank" shortcut state — opens unified BankPaymentDialog
  const [showBankPay, setShowBankPay] = useState(false);
  const [bankPaySeed, setBankPaySeed] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showJobTitleManager, setShowJobTitleManager] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [payingEmp, setPayingEmp] = useState(null);
  const [leaveEmp, setLeaveEmp] = useState(null);
  const [empSummary, setEmpSummary] = useState(null);
  const [formData, setFormData] = useState({ name: '', employee_number: '', document_id: '', phone: '', email: '', position: '', job_title_id: '', department: '', branch_id: '', salary: '', pay_frequency: 'monthly', join_date: '', document_expiry: '', annual_leave_entitled: 30, sick_leave_entitled: 15, pos_role: '', notes: '', nationality: '', gender: '', date_of_birth: '', marital_status: '', bank_name: '', bank_account_number: '', bank_iban: '', bank_accounts: [], emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '', contract_type: '', contract_end_date: '', probation_end_date: '', basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowance: '', country_code: '' });
  const [payData, setPayData] = useState({ payment_type: 'salary', amount: '', payment_mode: 'cash', branch_id: '', period: '', date: localToday(), notes: '' });
  const [payBanks, setPayBanks] = useState([]);
  useEffect(() => { api.get('/bank-accounts').then(r => setPayBanks((Array.isArray(r.data) ? r.data : []).filter(a => a.active !== false))).catch(() => {}); }, []);
  const [salaryMeta, setSalaryMeta] = useState({ effective_date: '', reason: '' });
  const [monthsVisible, setMonthsVisible] = useState(6);
  // iter778 — smart pre-payment salary alerts
  const [salaryAlerts, setSalaryAlerts] = useState([]);
  const [deductions, setDeductions] = useState(null);
  const [statusEmp, setStatusEmp] = useState(null);
  const [transferEmp, setTransferEmp] = useState(null);
  const [transferForm, setTransferForm] = useState({ to_branch_id: '', effective_date: localToday(), reason: '' });
  const [transferHistory, setTransferHistory] = useState([]);
  const [transferSaving, setTransferSaving] = useState(false);

  const [showLabour, setShowLabour] = useState(false);
  const [labourMonth, setLabourMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [labourData, setLabourData] = useState(null);
  const [labourLoading, setLabourLoading] = useState(false);

  const monthToPeriod = (ym) => {
    const [y, m] = (ym || '').split('-');
    if (!y || !m) return '';
    return format(new Date(Number(y), Number(m) - 1, 1), 'MMM yyyy');
  };

  const fetchLabour = async (ym) => {
    const period = monthToPeriod(ym);
    if (!period) return;
    setLabourLoading(true);
    try {
      const { data } = await api.get('/reports/labour-cost-by-branch', { params: { period } });
      setLabourData(data);
    } catch (e) {
      toast.error(`Labour report failed (HTTP ${e.response?.status ?? 'network'}): ${e.response?.data?.detail || e.message}`);
      setLabourData(null);
    } finally { setLabourLoading(false); }
  };

  const toggleLabour = () => {
    const next = !showLabour;
    setShowLabour(next);
    if (next) fetchLabour(labourMonth);
  };

  const exportLabour = () => {
    const rows = (labourData?.rows || []).map((r) => ({
      branch: r.branch_name, payments: r.count, cost: Number(r.amount) || 0,
    }));
    handleExport(rows, `labour_by_branch_${labourMonth}`, [
      { key: 'branch', label: 'Branch' },
      { key: 'payments', label: 'Payments' },
      { key: 'cost', label: 'Staff Cost (SAR)' },
    ], 'branches', 'cost');
  };

  const openTransfer = async (emp) => {
    setTransferEmp(emp);
    setTransferForm({ to_branch_id: '', effective_date: localToday(), reason: '' });
    try {
      const { data } = await api.get(`/employees/${emp.id}/branch-history`);
      setTransferHistory(data || []);
    } catch { setTransferHistory([]); }
  };

  const submitTransfer = async () => {
    if (!transferForm.to_branch_id) { toast.error('Select the destination branch'); return; }
    if (!transferForm.effective_date) { toast.error('Effective date is required'); return; }
    setTransferSaving(true);
    try {
      await api.post(`/employees/${transferEmp.id}/transfer-branch`, transferForm);
      toast.success(`${transferEmp.name} transferred to ${branchName(transferForm.to_branch_id)}`);
      setTransferEmp(null);
      fetchData();
    } catch (e) {
      toast.error(`Transfer failed (HTTP ${e.response?.status ?? 'network'}): ${e.response?.data?.detail || e.message}`);
    } finally { setTransferSaving(false); }
  };
  useEffect(() => {
    if (!showPayDialog || !payingEmp || payData.payment_type !== 'salary' || !payData.period) {
      setSalaryAlerts([]); setDeductions(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/salary-alerts', { params: { employee_id: payingEmp.id, period: payData.period, amount: parseFloat(payData.amount) || 0 } });
        setSalaryAlerts(data.alerts || []);
        setDeductions(data.deductions || null);
      } catch { setSalaryAlerts([]); setDeductions(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [showPayDialog, payingEmp, payData.payment_type, payData.period, payData.amount]);
  const [editPay, setEditPay] = useState(null);
  const saveEditPay = async () => {
    if (!editPay) return;
    try {
      await api.put(`/salary-payments/${editPay.id}`, {
        amount: parseFloat(editPay.amount),
        date: editPay.date,
        payment_mode: editPay.payment_mode,
        notes: editPay.notes,
      });
      toast.success('Payment corrected. Linked expense updated.');
      setEditPay(null);
      if (empSummary?.employee) viewSummary(empSummary.employee);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };
  const [leaveData, setLeaveData] = useState({ leave_type: 'annual', start_date: '', end_date: '', days: '', reason: '' });
  const [pinLoading, setPinLoading] = useState(null);

  const handleGeneratePin = async (empId, empName) => {
    if (!empId) { toast.error('Please save the employee first before generating a PIN.'); return; }
    setPinLoading(empId);
    try {
      const res = await api.post(`/cashier/generate-pin/${empId}`);
      toast.success(`PIN for ${empName}: ${res.data.pin}`);
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to generate PIN';
      toast.error(msg);
    }
    finally { setPinLoading(null); }
  };

  const handleRevokePin = async (empId, empName) => {
    if (!window.confirm(`Revoke PIN for ${empName}? They won't be able to access Cashier/Waiter portals.`)) return;
    setPinLoading(empId);
    try {
      await api.delete(`/cashier/pin/${empId}`);
      toast.success(`PIN revoked for ${empName}`);
      fetchData();
    } catch { toast.error('Failed to revoke PIN'); }
    finally { setPinLoading(null); }
  };

  const copyPin = (pin) => {
    navigator.clipboard.writeText(pin);
    toast.success('PIN copied to clipboard');
  };
  const [newJobTitle, setNewJobTitle] = useState({ title: '', department: '', min_salary: '', max_salary: '', permissions: [] });
  const [editingJT, setEditingJT] = useState(null);
  const [resignDialog, setResignDialog] = useState(null);
  const [resignForm, setResignForm] = useState({ resignation_date: '', notice_period_days: 30, reason: '', status: 'resigned' });
  const [settlementDialog, setSettlementDialog] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [clearanceUpdating, setClearanceUpdating] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const ALL_PERMISSIONS = [
    { key: 'dashboard', label: 'Dashboard' }, { key: 'sales', label: 'Sales' }, { key: 'invoices', label: 'Invoices' },
    { key: 'branches', label: 'Branches' }, { key: 'customers', label: 'Customers' }, { key: 'suppliers', label: 'Suppliers' },
    { key: 'supplier_payments', label: 'Supplier Payments' }, { key: 'expenses', label: 'Expenses' },
    { key: 'cash_transfers', label: 'Cash Transfers' }, { key: 'employees', label: 'Employees' },
    { key: 'stock', label: 'Stock' }, { key: 'kitchen', label: 'Kitchen' }, { key: 'shifts', label: 'Schedule' },
    { key: 'documents', label: 'Documents' }, { key: 'reports', label: 'Reports' },
    { key: 'credit_report', label: 'Credit Report' }, { key: 'supplier_report', label: 'Supplier Report' },
  ];

  useEffect(() => { fetchData(); }, []);

  // iter839 — Deep-link auto-open: when the Payroll Salary Status tab's
  // "Pay Rest" button navigates here with ?payRemaining=1&empId=X&period=YYYY-MM&amount=Y,
  // load the employee's summary and open the Pay dialog prefilled with
  // the remaining balance for that specific period.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('payRemaining') !== '1') return;
    const empId = url.searchParams.get('empId');
    const period = url.searchParams.get('period') || '';
    const amount = url.searchParams.get('amount') || '';
    if (!empId) return;
    (async () => {
      try {
        const { data: emps } = await api.get('/employees');
        const emp = (emps || []).find((e) => e.id === empId);
        if (!emp) { toast.error('Employee not found'); return; }
        setPayingEmp(emp);
        setPayData({
          payment_type: 'salary',
          amount: String(amount || (emp.salary || 0)),
          payment_mode: 'cash',
          branch_id: emp.branch_id || '',
          period,
          date: localToday(),
          notes: `Remaining balance for ${period}`,
        });
        setShowPayDialog(true);
        // Clean the URL so a refresh doesn't re-trigger
        url.searchParams.delete('payRemaining');
        url.searchParams.delete('empId');
        url.searchParams.delete('period');
        url.searchParams.delete('amount');
        window.history.replaceState({}, '', url.toString());
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchData = async () => {
    try {
      fetchBranches();
      const [empRes, spRes, pendRes, jtRes] = await Promise.allSettled([
        api.get('/employees'),
        api.get('/salary-payments'),
        api.get('/employees/pending-summary'),
        api.get('/job-titles'),
      ]);
      if (empRes.status === 'fulfilled' && Array.isArray(empRes.value?.data)) setEmployees(empRes.value.data);
      else if (empRes.status === 'fulfilled' && empRes.value?.data) setEmployees([]);
      if (spRes.status === 'fulfilled') setSalaryPayments(Array.isArray(spRes.value?.data) ? spRes.value.data : []);
      if (pendRes.status === 'fulfilled') {
        const pd = pendRes.value?.data;
        setPendingSummary(Array.isArray(pd?.employees) ? pd.employees : Array.isArray(pd) ? pd : []);
      }
      if (jtRes.status === 'fulfilled') setJobTitles(Array.isArray(jtRes.value?.data) ? jtRes.value.data : []);
      // Only show error if primary employee fetch failed
      if (empRes.status === 'rejected') {
        console.error('employees fetch failed:', empRes.reason);
        toast.error('Failed to load employees');
      }
    } catch (err) { console.error('fetchData error:', err); toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.subject || !emailForm.message) { toast.error('Subject and message required'); return; }
    setSendingEmail(true);
    try {
      const res = await api.post(`/employees/${emailDialog.id}/send-email`, emailForm);
      if (res.data.status === 'sent') toast.success(res.data.message);
      else toast.error(res.data.message);
      setEmailDialog(null);
      setEmailForm({ subject: '', message: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send email'); }
    finally { setSendingEmail(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const salaryChanged = editingEmp && Math.abs((parseFloat(formData.salary) || 0) - (parseFloat(editingEmp.salary) || 0)) > 0.001;
    if (salaryChanged && !salaryMeta.effective_date) { toast.error('Effective Date is required when changing salary'); return; }
    setSubmitting(true);
    try {
      const payload = { ...formData, ...(salaryChanged ? { salary_effective_date: salaryMeta.effective_date, salary_change_reason: salaryMeta.reason } : {}), salary: parseFloat(formData.salary) || 0, annual_leave_entitled: parseInt(formData.annual_leave_entitled) || 30, sick_leave_entitled: parseInt(formData.sick_leave_entitled) || 15, join_date: formData.join_date ? new Date(formData.join_date).toISOString() : null, document_expiry: formData.document_expiry ? new Date(formData.document_expiry).toISOString() : null, branch_id: formData.branch_id || null, basic_salary: parseFloat(formData.basic_salary) || null, housing_allowance: parseFloat(formData.housing_allowance) || null, transport_allowance: parseFloat(formData.transport_allowance) || null, other_allowance: parseFloat(formData.other_allowance) || null };
      if (editingEmp) { await api.put(`/employees/${editingEmp.id}`, payload); toast.success('Employee updated (email synced to user account)'); }
      else { await api.post('/employees', payload); toast.success('Employee added successfully'); }
      setShowDialog(false); resetForm(); setBranchFilter([]); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save employee'); }
    finally { setSubmitting(false); }
  };

  const handlePaySalary = async (e) => {
    e.preventDefault();
    // iter927 — Mandatory Branch guardrail: a salary payment must be tied to a branch.
    if (!payData.branch_id) { toast.error('Please select a Branch before saving'); return; }
    try {
      const { data: payRes } = await api.post('/salary-payments', { ...payData, employee_id: payingEmp.id, amount: parseFloat(payData.amount), branch_id: payData.branch_id || null, date: `${payData.date}T12:00:00` });
      const tl = PAYMENT_TYPES.find(t => t.value === payData.payment_type)?.label || 'Payment';
      toast.success(`${tl} recorded for ${payingEmp.name}`);
      if (payRes?.journal_status && !['posted', 'linked'].includes(payRes.journal_status)) {
        toast.warning('Saved, but the accounting journal was NOT posted — it is on hold for accountant review (Accounting page).', { duration: 10000 });
      }
      if (['tickets', 'id_card'].includes(payData.payment_type)) toast.info('Also added to Expenses');
      setShowPayDialog(false);
      setPayData({ payment_type: 'salary', amount: '', payment_mode: 'cash', branch_id: '', period: '', date: localToday(), notes: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leaves', { ...leaveData, employee_id: leaveEmp.id, days: parseInt(leaveData.days), start_date: new Date(leaveData.start_date).toISOString(), end_date: new Date(leaveData.end_date).toISOString() });
      toast.success('Leave recorded');
      setShowLeaveDialog(false);
      setLeaveData({ leave_type: 'annual', start_date: '', end_date: '', days: '', reason: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadPayslip = async (paymentId) => {
    try {
      const res = await api.get(`/salary-payments/${paymentId}/payslip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch { toast.error('Failed to download payslip'); }
  };

  const downloadEmpReport = async (empId) => {
    try {
      const res = await api.get(`/employees/${empId}/report/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'employee_report.pdf'); document.body.appendChild(link); link.click(); link.remove();
      toast.success('Report downloaded');
    } catch { toast.error('Failed'); }
  };

  const getMonthOptions = () => {
    const months = [];
    const seen = new Set();
    for (let i = -2; i <= 24; i++) {
      const d = new Date();
      d.setDate(1); // Prevent month overflow (e.g. Mar 31 → setMonth(3) → Apr 31 → May 1)
      d.setMonth(d.getMonth() - i);
      const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (!seen.has(label)) { seen.add(label); months.push(label); }
    }
    return months;
  };

  const [empDocs, setEmpDocs] = useState([]);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [newDoc, setNewDoc] = useState({ document_type: 'passport', document_number: '', expiry_date: '' });
  const [newIncrement, setNewIncrement] = useState({ new_salary: '', effective_date: '', reason: '' });
  // iter914 — What-If preview result (pro-rated cost of a proposed raise).
  const [whatIfPreview, setWhatIfPreview] = useState(null);
  const [whatIfLoading, setWhatIfLoading] = useState(false);

  const viewSummary = async (emp) => {
    setMonthsVisible(6);
    try {
      const [res, docsRes, histRes] = await Promise.allSettled([
        api.get(`/employees/${emp.id}/summary`),
        api.get(`/employee-documents?employee_id=${emp.id}`),
        api.get(`/salary-history/${emp.id}`),
      ]);
      if (res.status === 'fulfilled') setEmpSummary(res.value.data);
      else { setEmpSummary({ employee: emp }); console.error('Summary failed:', res.reason); }
      setEmpDocs(docsRes.status === 'fulfilled' ? (docsRes.value.data || []) : []);
      setSalaryHistory(histRes.status === 'fulfilled' ? (histRes.value.data || []) : []);
      setShowSummary(true);
    } catch (e) { toast.error('Failed to load summary'); console.error(e); }
  };

  const handleAddEmpDoc = async () => {
    if (!empSummary?.employee?.id || !newDoc.document_type) return;
    try {
      await api.post('/employee-documents', { ...newDoc, employee_id: empSummary.employee.id, expiry_date: newDoc.expiry_date ? new Date(newDoc.expiry_date).toISOString() : null });
      toast.success('Document added');
      const docsRes = await api.get(`/employee-documents?employee_id=${empSummary.employee.id}`);
      setEmpDocs(docsRes.data);
      setNewDoc({ document_type: 'passport', document_number: '', expiry_date: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (emp) => {
    setEditingEmp(emp);
    setFormData({ name: emp.name, employee_number: emp.employee_number || '', document_id: emp.document_id || '', phone: emp.phone || '', email: emp.email || '', position: emp.position || '', job_title_id: emp.job_title_id || '', department: emp.department || '', branch_id: emp.branch_id || '', salary: emp.salary || '', pay_frequency: emp.pay_frequency || 'monthly', join_date: emp.join_date ? toLocalYmd(new Date(emp.join_date)) : '', document_expiry: emp.document_expiry ? toLocalYmd(new Date(emp.document_expiry)) : '', annual_leave_entitled: emp.annual_leave_entitled || 30, sick_leave_entitled: emp.sick_leave_entitled || 15, pos_role: emp.pos_role || '', notes: emp.notes || '', nationality: emp.nationality || '', gender: emp.gender || '', date_of_birth: emp.date_of_birth || '', marital_status: emp.marital_status || '', bank_name: emp.bank_name || '', bank_account_number: emp.bank_account_number || '', bank_iban: emp.bank_iban || '', bank_accounts: emp.bank_accounts || [], preferred_payment_mode: emp.preferred_payment_mode || '', emergency_contact_name: emp.emergency_contact_name || '', emergency_contact_phone: emp.emergency_contact_phone || '', emergency_contact_relation: emp.emergency_contact_relation || '', contract_type: emp.contract_type || '', contract_end_date: emp.contract_end_date || '', probation_end_date: emp.probation_end_date || '', basic_salary: emp.basic_salary || '', housing_allowance: emp.housing_allowance || '', transport_allowance: emp.transport_allowance || '', other_allowance: emp.other_allowance || '', country_code: emp.country_code || '' });
    setShowDialog(true);
  };

  const handleInvitePortal = async (emp) => {
    try {
      // Check if already invited
      const status = await api.get(`/employee-portal/invite-status/${emp.id}`);
      if (status.data.invited) {
        if (!window.confirm(`${emp.name} already has portal access (${status.data.email}). Revoke and re-invite?`)) return;
        await api.delete(`/employee-portal/invite/${emp.id}`);
      }
      const email = window.prompt('Email for portal login (defaults to employee email):', emp.email || '');
      if (email === null) return; // user cancelled
      const r = await api.post(`/employee-portal/invite/${emp.id}`, { email: email || undefined });
      const { temp_password } = r.data;
      // Show in a copyable confirm box
      window.alert(
        `Portal access created for ${emp.name}.\n\n` +
        `Email: ${r.data.email}\n` +
        `Temporary password: ${temp_password}\n\n` +
        `IMPORTANT: Share these credentials securely. The employee will be required to change the password on first login.`
      );
      toast.success(`Invite sent to ${r.data.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to invite');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this employee?')) {
      try { await api.delete(`/employees/${id}`); toast.success('Deleted'); fetchData(); }
      catch { toast.error('Failed'); }
    }
  };

  const resetForm = () => { setFormData({ name: '', employee_number: '', document_id: '', phone: '', email: '', position: '', job_title_id: '', department: '', branch_id: '', salary: '', pay_frequency: 'monthly', join_date: '', document_expiry: '', annual_leave_entitled: 30, sick_leave_entitled: 15, pos_role: '', notes: '', nationality: '', gender: '', date_of_birth: '', marital_status: '', bank_name: '', bank_account_number: '', bank_iban: '', bank_accounts: [], emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '', contract_type: '', contract_end_date: '', probation_end_date: '', basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowance: '', country_code: '' }); setEditingEmp(null); setSalaryMeta({ effective_date: '', reason: '' }); };

  const isExpiryNear = (d) => d && Math.floor((new Date(d) - new Date()) / 86400000) <= 30;
  const getEmpTotalPaid = (id) => salaryPayments.filter(p => p.employee_id === id).reduce((s, p) => s + p.amount, 0);
  const getPending = (id) => pendingSummary.find(p => p.id === id) || {};

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  const totalSalary = employees.filter(e => e.active !== false).reduce((s, e) => s + (e.salary || 0), 0);
  const totalLoan = employees.reduce((s, e) => s + (e.loan_balance || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Users size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="employees-page-title">Employees</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Payroll, loans, leaves & document tracking</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-2 text-[13px] rounded-xl border border-border bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 w-52"
                data-testid="employee-search-input"
              />
            </div>
            <BranchFilter onChange={setBranchFilter} />
            <PageTour storageKey="tour_employees" steps={[
              { target: '[data-testid="add-employee-button"]', title: 'Add Employee', content: 'Create new employee profiles with personal details, salary, role, and branch assignment.' },
              { target: '[data-testid="manage-job-titles-btn"]', title: 'Job Titles', content: 'Manage job titles and positions used across your organization.' },
              { target: '[data-testid="employee-search"]', title: 'Search & Filter', content: 'Search employees by name or role. Filter by branch to see specific teams.' },
            ]} />
            <ExportButtons branchIds={branchFilter} dataType="employees" filters={{ branch_ids: branchFilter, status: statusFilter }} />
            <BulkSalaryPayment onComplete={fetchData} />
            <Button size="sm" variant="outline" className="rounded-xl border-border text-[13px] h-9" onClick={exportAllSalaries} data-testid="export-all-salaries-btn"><DollarSign size={14} className="mr-1.5" />Export All Salaries</Button>
            <Button size="sm" variant="outline" className={`rounded-xl text-[13px] h-9 ${showLabour ? 'border-sky-400/60 text-sky-600' : 'border-border'}`} onClick={toggleLabour} data-testid="labour-by-branch-toggle"><Banknote size={14} className="mr-1.5" />Labour by Branch</Button>
            {canRepair && (
              <Button size="sm" variant="outline" className="rounded-xl border-warning/40 text-warning text-[13px] h-9" onClick={repairSalaryHistory} data-testid="repair-salary-history-btn"><RefreshCw size={14} className="mr-1.5" />Repair Salary History</Button>
            )}
            <Button size="sm" variant="outline" className="rounded-xl border-border text-[13px] h-9" onClick={() => setShowJobTitleManager(true)} data-testid="manage-job-titles-btn-2"><Briefcase size={14} className="mr-1.5" />Job Titles</Button>
            <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild><Button size="sm" className="rounded-xl bg-primary hover:bg-primary/90 text-[13px] h-9 shadow-sm shadow-primary/20" data-testid="add-employee-button-2"><Plus size={14} className="mr-1.5" />Add Employee</Button></DialogTrigger>
              <DialogContent className="max-w-3xl" data-testid="employee-dialog">
                <DialogHeader><DialogTitle className="font-outfit">{editingEmp ? 'Edit' : 'Add'} Employee</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 max-h-[65dvh] overflow-y-auto pr-2">
                  <Tabs defaultValue="basic" className="space-y-3">
                    <TabsList className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 w-full">
                      <TabsTrigger value="basic" data-testid="emp-tab-basic">Basic Info</TabsTrigger>
                      <TabsTrigger value="personal" data-testid="emp-tab-personal">Personal</TabsTrigger>
                      <TabsTrigger value="compensation" data-testid="emp-tab-compensation">Compensation</TabsTrigger>
                      <TabsTrigger value="contract" data-testid="emp-tab-contract">Contract & Bank</TabsTrigger>
                    </TabsList>
                    <TabsContent value="basic">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Name *</Label><Input value={formData.name} data-testid="emp-name" onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                        <div><Label>Employee Number</Label><Input value={formData.employee_number} data-testid="emp-number" onChange={(e) => setFormData({ ...formData, employee_number: e.target.value })} placeholder="HR-verified, unique (used to confirm salary)" /></div>
                        <div><Label>Document ID</Label><Input value={formData.document_id} data-testid="emp-doc-id" onChange={(e) => setFormData({ ...formData, document_id: e.target.value })} /></div>
                        <div><Label>Job Title</Label>
                          <Select value={formData.job_title_id || "none"} onValueChange={(v) => {
                            const jt = jobTitles.find(j => j.id === v);
                            const updates = { job_title_id: v === "none" ? "" : v, position: jt?.title || '' };
                            if (jt && !formData.salary && jt.min_salary > 0) updates.salary = jt.min_salary;
                            setFormData({ ...formData, ...updates });
                          }}>
                            <SelectTrigger data-testid="employee-job-title"><SelectValue placeholder="Select Job Title" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Title</SelectItem>
                              {jobTitles.filter(j => j.active !== false).map(j => <SelectItem key={j.id} value={j.id}>{j.title} {j.department ? `(${j.department})` : ''}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label>Department</Label><Input value={formData.department} data-testid="emp-department" onChange={(e) => setFormData({ ...formData, department: e.target.value })} /></div>
                        <div><Label>Branch</Label>
                          <Select value={formData.branch_id || "all"} onValueChange={(v) => setFormData({ ...formData, branch_id: v === "all" ? "" : v })}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent><SelectItem value="all">No Branch</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
                        <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
                        <div><Label>Join Date</Label><Input type="date" value={formData.join_date} onChange={(e) => setFormData({ ...formData, join_date: e.target.value })} /></div>
                        <div><Label>Document Expiry</Label><Input type="date" value={formData.document_expiry} onChange={(e) => setFormData({ ...formData, document_expiry: e.target.value })} /></div>
                        <div>
                          <Label>POS Role</Label>
                          <Select value={formData.pos_role || "none"} onValueChange={(v) => setFormData({ ...formData, pos_role: v === "none" ? "" : v })}>
                            <SelectTrigger data-testid="pos-role-select"><SelectValue placeholder="No POS access" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No POS Access</SelectItem>
                              <SelectItem value="cashier">Cashier Only</SelectItem>
                              <SelectItem value="waiter">Waiter Only</SelectItem>
                              <SelectItem value="both">Cashier + Waiter</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="personal">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Nationality</Label><Input value={formData.nationality} data-testid="emp-nationality" onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} placeholder="e.g. Saudi, Indian" /></div>
                        <div><Label>Gender</Label>
                          <Select value={formData.gender || "not_set"} onValueChange={(v) => setFormData({ ...formData, gender: v === "not_set" ? "" : v })}>
                            <SelectTrigger data-testid="emp-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent><SelectItem value="not_set">Not Set</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div><Label>Date of Birth</Label><Input type="date" value={formData.date_of_birth} data-testid="emp-dob" onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} /></div>
                        <div><Label>Marital Status</Label>
                          <Select value={formData.marital_status || "not_set"} onValueChange={(v) => setFormData({ ...formData, marital_status: v === "not_set" ? "" : v })}>
                            <SelectTrigger data-testid="emp-marital"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent><SelectItem value="not_set">Not Set</SelectItem><SelectItem value="single">Single</SelectItem><SelectItem value="married">Married</SelectItem><SelectItem value="divorced">Divorced</SelectItem><SelectItem value="widowed">Widowed</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2 border-t pt-3 mt-1"><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Emergency Contact</Label></div>
                        <div><Label>Contact Name</Label><Input value={formData.emergency_contact_name} data-testid="emp-emg-name" onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} /></div>
                        <div><Label>Contact Phone</Label><Input value={formData.emergency_contact_phone} data-testid="emp-emg-phone" onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} /></div>
                        <div><Label>Relationship</Label><Input value={formData.emergency_contact_relation} data-testid="emp-emg-relation" onChange={(e) => setFormData({ ...formData, emergency_contact_relation: e.target.value })} placeholder="e.g. Spouse, Parent" /></div>
                      </div>
                    </TabsContent>
                    <TabsContent value="compensation">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Total Monthly Salary</Label><Input type="number" step="0.01" value={formData.salary} data-testid="emp-salary" onChange={(e) => setFormData({ ...formData, salary: e.target.value })} /></div>
                        {editingEmp && Math.abs((parseFloat(formData.salary) || 0) - (parseFloat(editingEmp.salary) || 0)) > 0.001 && (<>
                          <div><Label>Salary Effective Date *</Label><Input type="date" value={salaryMeta.effective_date} data-testid="emp-salary-effective-date" onChange={(e) => setSalaryMeta({ ...salaryMeta, effective_date: e.target.value })} /><p className="text-[10px] text-muted-foreground mt-1">Required — payroll pro-rates this month from this date.</p></div>
                          <div><Label>Change Reason</Label><Input value={salaryMeta.reason} data-testid="emp-salary-change-reason" onChange={(e) => setSalaryMeta({ ...salaryMeta, reason: e.target.value })} placeholder="e.g. Annual raise" /></div>
                        </>)}
                        <div><Label>Pay Frequency</Label>
                          <Select value={formData.pay_frequency} onValueChange={(v) => setFormData({ ...formData, pay_frequency: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Bi-weekly</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2 border-t pt-3 mt-1"><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Salary Breakdown</Label></div>
                        <div><Label>Basic Salary</Label><Input type="number" step="0.01" value={formData.basic_salary} data-testid="emp-basic-salary" onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })} /></div>
                        <div><Label>Housing Allowance</Label><Input type="number" step="0.01" value={formData.housing_allowance} data-testid="emp-housing" onChange={(e) => setFormData({ ...formData, housing_allowance: e.target.value })} /></div>
                        <div><Label>Transport Allowance</Label><Input type="number" step="0.01" value={formData.transport_allowance} data-testid="emp-transport" onChange={(e) => setFormData({ ...formData, transport_allowance: e.target.value })} /></div>
                        <div><Label>Other Allowance</Label><Input type="number" step="0.01" value={formData.other_allowance} data-testid="emp-other-allowance" onChange={(e) => setFormData({ ...formData, other_allowance: e.target.value })} /></div>
                        <div className="col-span-2 border-t pt-3 mt-1"><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Leave Entitlements</Label></div>
                        <div><Label>Annual Leave (days/yr)</Label><Input type="number" value={formData.annual_leave_entitled} onChange={(e) => setFormData({ ...formData, annual_leave_entitled: e.target.value })} /></div>
                        <div><Label>Sick Leave (days/yr)</Label><Input type="number" value={formData.sick_leave_entitled} onChange={(e) => setFormData({ ...formData, sick_leave_entitled: e.target.value })} /></div>
                      </div>
                    </TabsContent>
                    <TabsContent value="contract">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Contract Type</Label>
                          <Select value={formData.contract_type || "not_set"} onValueChange={(v) => setFormData({ ...formData, contract_type: v === "not_set" ? "" : v })}>
                            <SelectTrigger data-testid="emp-contract-type"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent><SelectItem value="not_set">Not Set</SelectItem><SelectItem value="unlimited">Unlimited</SelectItem><SelectItem value="limited">Limited/Fixed-term</SelectItem><SelectItem value="part_time">Part-time</SelectItem><SelectItem value="probation">Probation</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div><Label>Country (Labor Law)</Label>
                          <Input value={formData.country_code} data-testid="emp-country" onChange={(e) => setFormData({ ...formData, country_code: e.target.value })} placeholder="e.g. SA, AE, US" />
                        </div>
                        <div><Label>Contract End Date</Label><Input type="date" value={formData.contract_end_date} data-testid="emp-contract-end" onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })} /></div>
                        <div><Label>Probation End Date</Label><Input type="date" value={formData.probation_end_date} data-testid="emp-probation-end" onChange={(e) => setFormData({ ...formData, probation_end_date: e.target.value })} /></div>
                        <div className="col-span-2 border-t pt-3 mt-1"><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bank Details (for Salary Transfer)</Label></div>
                        <div><Label>Bank Name</Label><Input value={formData.bank_name} data-testid="emp-bank-name" onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} /></div>
                        <div><Label>Account Number</Label><Input value={formData.bank_account_number} data-testid="emp-bank-account" onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })} /></div>
                        <div className="col-span-2"><Label>IBAN</Label><Input value={formData.bank_iban} data-testid="emp-iban" onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })} placeholder="e.g. SA00 0000 0000 0000 0000 0000" /></div>
                        {/* iter863 — Preferred salary payment mode (wins over branch default in Bulk Salary auto-suggest) */}
                        <div className="col-span-2">
                          <Label>Preferred salary payment mode</Label>
                          <Select
                            value={formData.preferred_payment_mode || 'inherit'}
                            onValueChange={v => setFormData({ ...formData, preferred_payment_mode: v === 'inherit' ? '' : v })}
                          >
                            <SelectTrigger data-testid="emp-preferred-payment-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inherit">Use branch default</SelectItem>
                              <SelectItem value="bank">Bank transfer</SelectItem>
                              <SelectItem value="cash">Cash</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground mt-1">If set, overrides the branch default when Bulk Salary → &quot;Auto-suggest&quot; runs.</p>
                        </div>
                        <div className="col-span-2 mt-2 border-t pt-3">
                          <BankAccountsEditor
                            value={formData.bank_accounts}
                            onChange={(arr) => setFormData({ ...formData, bank_accounts: arr })}
                            testidPrefix="emp-bank"
                          />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                  <div className="flex gap-3">
                    <Button type="submit" disabled={submitting} data-testid="submit-employee" className="rounded-full">{submitting ? 'Saving...' : (editingEmp ? 'Update' : 'Add')} Employee</Button>
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Total</p>
              <p className="text-2xl font-bold font-outfit text-foreground mt-1">{employees.length}</p>
            </CardContent>
          </Card>
          <Card className={`stat-card border-0 bg-white cursor-pointer ${statusFilter === 'active' ? 'ring-2 ring-emerald-400/60 shadow-emerald-100' : ''}`} onClick={() => setStatusFilter('active')} data-testid="filter-active">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-success uppercase tracking-[0.08em]">Active</p>
              <p className="text-2xl font-bold font-outfit text-success mt-1">{employees.filter(e => !e.status || e.status === 'active').length}</p>
            </CardContent>
          </Card>
          <Card className={`stat-card border-0 bg-white cursor-pointer ${statusFilter === 'left' ? 'ring-2 ring-red-400/60 shadow-red-100' : ''}`} onClick={() => setStatusFilter('left')} data-testid="filter-left">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-red-400 uppercase tracking-[0.08em]">Left / Terminated</p>
              <p className="text-2xl font-bold font-outfit text-error mt-1">{employees.filter(e => ['left', 'terminated', 'resigned', 'end_of_contract'].includes(e.status)).length}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Monthly Payroll</p>
              <p className="text-2xl font-bold font-outfit text-warning mt-1">{cur()} {totalSalary.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
            </CardContent>
          </Card>
          <Card className={`stat-card border-0 bg-white cursor-pointer ${statusFilter === 'all' ? 'ring-2 ring-blue-400/60 shadow-blue-100' : ''}`} onClick={() => setStatusFilter('all')} data-testid="filter-all">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-info uppercase tracking-[0.08em]">Show All</p>
              <p className="text-2xl font-bold font-outfit text-info mt-1">{employees.length}</p>
            </CardContent>
          </Card>
        </div>

        {showLabour && (
          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white dark:bg-muted rounded-2xl overflow-hidden" data-testid="labour-by-branch-panel">
            <CardHeader className="pb-0 pt-5 px-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="font-outfit text-[15px] font-semibold text-foreground flex items-center gap-2"><Banknote size={16} className="text-sky-500" />Labour Cost by Branch</CardTitle>
                <div className="flex items-center gap-2">
                  <Input type="month" value={labourMonth} className="h-9 w-[150px] rounded-xl text-[13px]" data-testid="labour-month-input" onChange={(e) => { setLabourMonth(e.target.value); fetchLabour(e.target.value); }} />
                  <Button size="sm" variant="ghost" className="h-9 rounded-xl text-[12px]" onClick={() => fetchLabour(labourMonth)} data-testid="labour-refresh-btn"><RefreshCw size={13} className="mr-1" />Refresh</Button>
                  <Button size="sm" variant="outline" className="h-9 rounded-xl text-[12px] border-border" onClick={exportLabour} data-testid="labour-export-btn"><Download size={13} className="mr-1" />Export</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-6 pt-4 pb-5">
              {labourLoading ? (
                <p className="text-[13px] text-muted-foreground py-4">Loading…</p>
              ) : !labourData || (labourData.rows || []).length === 0 ? (
                <p className="text-[13px] text-muted-foreground py-4" data-testid="labour-empty">No salary cost recorded for {monthToPeriod(labourMonth)}.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]" data-testid="labour-table">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="pb-2 font-medium">Branch</th>
                        <th className="pb-2 font-medium text-center">Payments</th>
                        <th className="pb-2 font-medium text-right">Staff Cost ({monthToPeriod(labourMonth)})</th>
                        <th className="pb-2 font-medium text-right">% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(labourData.rows || []).map((r) => (
                        <tr key={r.branch_id || 'none'} className="border-b border-border/50" data-testid={`labour-row-${r.branch_id || 'none'}`}>
                          <td className="py-2 font-medium text-foreground">{r.branch_name}</td>
                          <td className="py-2 text-center text-muted-foreground">{r.count}</td>
                          <td className="py-2 text-right font-semibold text-foreground">SAR {Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="py-2 text-right text-muted-foreground">{labourData.total > 0 ? ((r.amount / labourData.total) * 100).toFixed(1) : '0.0'}%</td>
                        </tr>
                      ))}
                      <tr className="font-semibold text-foreground">
                        <td className="pt-2.5">Total</td>
                        <td className="pt-2.5 text-center">{(labourData.rows || []).reduce((s, r) => s + (r.count || 0), 0)}</td>
                        <td className="pt-2.5 text-right" data-testid="labour-total">SAR {Number(labourData.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="pt-2.5 text-right">100%</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="text-[11px] text-muted-foreground mt-3">Cost is attributed to the branch that bears it — mid-month transfers are split by days, and cross-branch funded payroll lands on the beneficiary branch.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white dark:bg-muted rounded-2xl overflow-hidden">
          <CardHeader className="pb-0 pt-5 px-6"><CardTitle className="font-outfit text-[15px] font-semibold text-foreground">All Employees</CardTitle></CardHeader>
          <CardContent className="px-0 pt-4">
            {/* Mobile card view */}
            <div className="sm:hidden space-y-2">
              {employees.filter(emp => {
                if (searchQuery && !(emp.name || '').toLowerCase().includes(searchQuery.toLowerCase()) && !(emp.email || '').toLowerCase().includes(searchQuery.toLowerCase()) && !(emp.phone || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
                if (branchFilter.length > 0 && !branchFilter.includes(emp.branch_id) && emp.branch_id) return false;
                if (statusFilter === 'active') return !emp.status || emp.status === 'active';
                if (statusFilter === 'left') return ['left', 'terminated', 'resigned', 'on_notice', 'end_of_contract'].includes(emp.status);
                return true;
              }).map((emp) => {
                const pend = getPending(emp.id);
                const jt = jobTitles.find(j => j.id === emp.job_title_id);
                return (
                  <div key={emp.id} className={`p-3 rounded-2xl space-y-2 ${emp.status === 'resigned' || emp.status === 'on_notice' || emp.status === 'end_of_contract' ? 'bg-warning/10 border border-warning/30' : emp.status === 'left' || emp.status === 'terminated' ? 'bg-error/10 border border-error/30' : 'bg-white shadow-sm'}`} data-testid={`employee-card-${emp.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-bold">{emp.name}</p>
                        {jt && <Badge variant="outline" className="capitalize text-[10px] mt-0.5">{jt.title}</Badge>}
                        {emp.status && emp.status !== 'active' && (
                          <Badge className={`ml-1 text-[10px] ${emp.status === 'resigned' || emp.status === 'on_notice' || emp.status === 'end_of_contract' ? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'}`}>{(emp.status || '').replace('_', ' ')}</Badge>
                        )}
                      </div>
                      <span className="text-sm font-bold text-foreground">{cur()} {(emp.salary || 0).toFixed(0)}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-center text-[10px]">
                      <div className="p-1.5 bg-muted/50 rounded-lg">
                        <p className="text-muted-foreground">Pending</p>
                        <p className={`font-bold ${(pend.pending_salary || 0) > 0 ? 'text-error' : 'text-success'}`}>{(pend.pending_salary || 0) > 0 ? `${cur()} ${pend.pending_salary.toFixed(0)}` : 'Paid'}</p>
                      </div>
                      <div className="p-1.5 bg-muted/50 rounded-lg">
                        <p className="text-muted-foreground">Loan</p>
                        <p className={`font-bold ${(emp.loan_balance || 0) > 0 ? 'text-warning' : 'text-muted-foreground'}`}>{(emp.loan_balance || 0) > 0 ? `${cur()} ${emp.loan_balance.toFixed(0)}` : '-'}</p>
                      </div>
                      <div className="p-1.5 bg-muted/50 rounded-lg">
                        <p className="text-muted-foreground">Leave</p>
                        <p className="font-bold"><span className="text-success">{pend.annual_leave_remaining || 0}A</span> <span className="text-info">{pend.sick_leave_remaining || 0}S</span></p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap pt-1 border-t border-border">
                      <Button size="sm" variant="outline" onClick={() => viewSummary(emp)} className="h-7 text-[10px] flex-1"><Eye size={10} className="mr-0.5" />View</Button>
                      <Button size="sm" variant="outline" onClick={() => { setPayingEmp(emp); setPayData(d => ({ ...d, amount: emp.salary || '', period: format(new Date(), 'MMM yyyy') })); setShowPayDialog(true); }} className="h-7 text-[10px] flex-1"><DollarSign size={10} className="mr-0.5" />Pay</Button>
                      <Button size="sm" variant="outline"
                        onClick={() => { setBankPaySeed({ payee_type: 'employee', payee_id: emp.id, payee_label: emp.name }); setShowBankPay(true); }}
                        data-testid={`pay-bank-emp-${emp.id}`}
                        className="h-7 text-[10px] flex-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        title="Pay this employee from a bank account"
                      ><Banknote size={10} className="mr-0.5" />Bank</Button>
                      <Button size="sm" variant="outline" onClick={() => { setLeaveEmp(emp); setShowLeaveDialog(true); }} className="h-7 text-[10px] flex-1"><Calendar size={10} className="mr-0.5" />Leave</Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(emp)} className="h-7 text-[10px]" data-testid={`edit-${emp.id}`}><Edit size={10} /></Button>
                      {(!emp.status || emp.status === 'active') && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-warning border-warning/30" data-testid="resign-card-btn"
                          onClick={() => { setResignDialog(emp); setResignForm({ resignation_date: localToday(), notice_period_days: 30, reason: '', status: 'resigned' }); }}>
                          <UserX size={10} className="mr-0.5" />Exit
                        </Button>
                      )}
                      {(emp.status === 'resigned' || emp.status === 'on_notice' || emp.status === 'terminated' || emp.status === 'end_of_contract') && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-info border-info/30" data-testid="settlement-card-btn"
                          onClick={async () => {
                            try { const { data } = await api.get(`/employees/${emp.id}/settlement`); setSettlement(data); setSettlementDialog(emp); } catch { toast.error('Failed'); }
                          }}>
                          <Calculator size={10} className="mr-0.5" />Settlement
                        </Button>
                      )}
                      {emp.status === 'left' && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-muted-foreground border-border" data-testid="review-card-btn"
                          onClick={async () => {
                            try { const { data } = await api.get(`/employees/${emp.id}/settlement`); setSettlement(data); setSettlementDialog(emp); } catch { toast.error('Failed'); }
                          }}>
                          <FileText size={10} className="mr-0.5" />Review
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {employees.length === 0 && <p className="text-center text-muted-foreground py-8">No employees yet</p>}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full" data-testid="employees-table">
                <thead><tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Job Title</th>
                  <th className="text-right">Salary</th>
                  <th className="text-right">Pending</th>
                  <th className="text-right">Loan</th>
                  <th className="text-center">Leave</th>
                  <th className="text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {employees.filter(emp => {
                    if (searchQuery) {
                      const q = searchQuery.toLowerCase();
                      if (!(emp.name || '').toLowerCase().includes(q) && !(emp.email || '').toLowerCase().includes(q) && !(emp.phone || '').toLowerCase().includes(q)) return false;
                    }
                    if (branchFilter.length > 0 && !branchFilter.includes(emp.branch_id) && emp.branch_id) return false;
                    if (statusFilter === 'active') return !emp.status || emp.status === 'active';
                    if (statusFilter === 'left') return ['left', 'terminated', 'resigned', 'on_notice', 'end_of_contract'].includes(emp.status);
                    return true;
                  }).map((emp) => {
                    const pend = getPending(emp.id);
                    return (
                    <tr key={emp.id} className={`group ${emp.status === 'resigned' || emp.status === 'on_notice' || emp.status === 'end_of_contract' ? 'bg-warning/10' : emp.status === 'left' || emp.status === 'terminated' ? 'bg-error/10' : ''}`} data-testid="employee-row">
                      <td className="font-medium text-foreground">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center text-muted-foreground text-xs font-bold shrink-0">
                            {emp.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <span className="block leading-tight">{emp.name}</span>
                            {emp.document_id && <span className="text-[11px] text-muted-foreground font-normal">{emp.document_id}</span>}
                            {emp.status && emp.status !== 'active' && (
                              <Badge className={`ml-0 mt-0.5 text-[9px] ${emp.status === 'resigned' || emp.status === 'on_notice' || emp.status === 'end_of_contract' ? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'}`}>
                                {(emp.status || '').replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{(() => { const jt = jobTitles.find(j => j.id === emp.job_title_id); return jt ? <Badge variant="outline" className="capitalize text-[11px] font-medium border-border">{jt.title}</Badge> : <span className="text-muted-foreground text-[13px]">{emp.position || '-'}</span>; })()}</td>
                      <td className="text-right font-semibold text-foreground tabular-nums">{cur()} {(emp.salary || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      <td className="text-right">
                        {(pend.pending_salary || 0) > 0 ? <span className="font-bold text-error tabular-nums">{cur()} {pend.pending_salary.toLocaleString(undefined, {minimumFractionDigits: 2})}</span> : <span className="text-success font-semibold text-[12px]">Paid</span>}
                      </td>
                      <td className="text-right">
                        {(emp.loan_balance || 0) > 0 ? <span className="font-bold text-warning tabular-nums">{cur()} {emp.loan_balance.toLocaleString(undefined, {minimumFractionDigits: 2})}</span> : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="text-center">
                        <div className="text-[11px] inline-flex items-center gap-1.5">
                          <span className="text-success font-semibold">{pend.annual_leave_remaining || 0}A</span>
                          <span className="text-info font-semibold">{pend.sick_leave_remaining || 0}S</span>
                          {pend.pending_leave_requests > 0 && <Badge className="bg-warning/20 text-warning text-[9px] h-4 px-1">{pend.pending_leave_requests}P</Badge>}
                        </div>
                      </td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end items-center opacity-80 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="ghost" onClick={() => viewSummary(emp)} data-testid="view-summary-btn" className="h-7 text-[11px] text-muted-foreground hover:text-primary hover:bg-primary/5 px-2"><Eye size={13} className="mr-1" />View</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setPayingEmp(emp); setPayData(d => ({ ...d, amount: emp.salary || '', period: format(new Date(), 'MMM yyyy') })); setShowPayDialog(true); }} data-testid="pay-salary-btn" className="h-7 text-[11px] text-muted-foreground hover:text-success hover:bg-success/10 px-2"><DollarSign size={13} className="mr-1" />Pay</Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-muted-foreground" data-testid={`emp-actions-${emp.id}`}>
                                <MoreHorizontal size={15} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => { setLeaveEmp(emp); setShowLeaveDialog(true); }} className="cursor-pointer"><Calendar size={14} className="mr-2 text-muted-foreground" />Record Leave</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setStatusEmp(emp)} className="cursor-pointer" data-testid={`salary-status-${emp.id}`}><CalendarDays size={14} className="mr-2 text-indigo-500" />Salary Status</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openTransfer(emp)} className="cursor-pointer" data-testid={`transfer-branch-${emp.id}`}><ArrowLeftRight size={14} className="mr-2 text-sky-500" />Transfer Branch</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(emp)} className="cursor-pointer" data-testid={`edit-menu-${emp.id}`}><Edit size={14} className="mr-2 text-muted-foreground" />Edit Employee</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleInvitePortal(emp)} className="cursor-pointer" data-testid={`invite-portal-${emp.id}`}><UserCheck size={14} className="mr-2 text-emerald-500" />Invite to My Portal</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => downloadEmpReport(emp.id)} className="cursor-pointer"><FileText size={14} className="mr-2 text-muted-foreground" />Download Report</DropdownMenuItem>
                              {emp.email && (
                                <DropdownMenuItem onClick={() => { setEmailDialog(emp); setEmailForm({ subject: '', message: '' }); }} className="cursor-pointer"><Mail size={14} className="mr-2 text-muted-foreground" />Send Email</DropdownMenuItem>
                              )}
                              {emp.cashier_pin ? (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => copyPin(emp.cashier_pin)} className="cursor-pointer"><Copy size={14} className="mr-2 text-violet-500" />Copy PIN: {emp.cashier_pin}</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleGeneratePin(emp.id, emp.name)} className="cursor-pointer"><RefreshCw size={14} className="mr-2 text-violet-500" />Regenerate PIN</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleRevokePin(emp.id, emp.name)} className="cursor-pointer text-error"><Trash2 size={14} className="mr-2" />Revoke PIN</DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem onClick={() => handleGeneratePin(emp.id, emp.name)} className="cursor-pointer"><KeyRound size={14} className="mr-2 text-violet-500" />Generate PIN</DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {(!emp.status || emp.status === 'active') && (
                                <DropdownMenuItem onClick={() => { setResignDialog(emp); setResignForm({ resignation_date: localToday(), notice_period_days: 30, reason: '', status: 'resigned' }); }} className="cursor-pointer text-warning"><UserX size={14} className="mr-2" />Process Exit</DropdownMenuItem>
                              )}
                              {(emp.status === 'resigned' || emp.status === 'on_notice' || emp.status === 'terminated' || emp.status === 'end_of_contract') && (
                                <DropdownMenuItem onClick={async () => { try { const { data } = await api.get(`/employees/${emp.id}/settlement`); setSettlement(data); setSettlementDialog(emp); } catch { toast.error('Failed'); } }} className="cursor-pointer text-info"><Calculator size={14} className="mr-2" />Settlement</DropdownMenuItem>
                              )}
                              {emp.status === 'left' && (
                                <DropdownMenuItem onClick={async () => { try { const { data } = await api.get(`/employees/${emp.id}/settlement`); setSettlement(data); setSettlementDialog(emp); } catch { toast.error('Failed'); } }} className="cursor-pointer"><FileText size={14} className="mr-2 text-muted-foreground" />Review Settlement</DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(emp.id)} className="cursor-pointer text-error focus:text-error"><Trash2 size={14} className="mr-2" />Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ); })}
                  {employees.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-muted-foreground text-[13px]">No employees yet. Click &quot;Add Employee&quot; to get started.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Recorded Payment Dialog (iter753) */}
        <Dialog open={!!editPay} onOpenChange={(o) => { if (!o) setEditPay(null); }}>
          <DialogContent data-testid="edit-pay-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Correct Payment</DialogTitle></DialogHeader>
            {editPay && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Amount *</Label><Input type="number" step="0.01" value={editPay.amount} onChange={(e) => setEditPay({ ...editPay, amount: e.target.value })} data-testid="edit-pay-amount" /></div>
                  <div><Label>Date</Label><Input type="date" value={editPay.date} onChange={(e) => setEditPay({ ...editPay, date: e.target.value })} data-testid="edit-pay-date" /></div>
                  <div><Label>Mode</Label><Select value={editPay.payment_mode} onValueChange={(v) => setEditPay({ ...editPay, payment_mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent></Select></div>
                </div>
                <div><Label>Notes</Label><Input value={editPay.notes} onChange={(e) => setEditPay({ ...editPay, notes: e.target.value })} /></div>
                <p className="text-[11px] text-muted-foreground">Correcting amount/date also updates the linked expense and adjusts the loan balance where applicable.</p>
                <Button onClick={saveEditPay} className="w-full" data-testid="edit-pay-save">Save Correction</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Pay Dialog */}
        <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
          <DialogContent data-testid="pay-salary-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Record Payment - {payingEmp?.name}</DialogTitle></DialogHeader>
            <div className="flex gap-4 text-sm mb-2 flex-wrap">
              <span>Salary: <span className="font-bold"> {cur()} {payingEmp?.salary?.toFixed(2)}</span></span>
              {(payingEmp?.loan_balance || 0) > 0 && <span>Loan: <span className="font-bold text-warning"> {cur()} {payingEmp?.loan_balance?.toFixed(2)}</span></span>}
              {(payingEmp?.old_salary_balance || 0) > 0 && <span>Old Balance: <span className="font-bold text-error"> {cur()} {payingEmp?.old_salary_balance?.toFixed(2)}</span></span>}
            </div>
            <form onSubmit={handlePaySalary} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Payment Type *</Label>
                  <Select value={payData.payment_type} onValueChange={(v) => setPayData({ ...payData, payment_type: v })}>
                    <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {['tickets', 'id_card'].includes(payData.payment_type) && <p className="text-xs text-warning mt-1">Also added to Expenses</p>}
                  {payData.payment_type === 'advance' && <p className="text-xs text-info mt-1">Will increase loan balance</p>}
                  {payData.payment_type === 'loan_repayment' && <p className="text-xs text-success mt-1">Will decrease loan balance</p>}
                </div>
                <div><Label>Amount *</Label><Input type="number" step="0.01" value={payData.amount} data-testid="salary-amount" onChange={(e) => setPayData({ ...payData, amount: e.target.value })} required /></div>
                <div><Label>Period (salary is for) *</Label>
                  <Select value={payData.period} onValueChange={(v) => setPayData({ ...payData, period: v })}>
                    <SelectTrigger data-testid="salary-period"><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>{getMonthOptions().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">Work month this salary covers (label only)</p>
                </div>
                <div><Label>Mode</Label>
                  <Select value={payData.payment_mode} onValueChange={(v) => setPayData({ ...payData, payment_mode: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Paid From <span className="text-error">*</span></Label>
                  <Select value={payData.branch_id || undefined} onValueChange={(v) => setPayData({ ...payData, branch_id: v })}>
                    <SelectTrigger data-testid="pay-branch-select"><SelectValue placeholder="Select a branch" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Required — which branch cash/bank is used to pay</p>
                </div>
                {payData.payment_mode === 'bank' && (
                  <div><Label>Bank account</Label>
                    <Select value={payData.bank_account_id || undefined} onValueChange={(v) => setPayData({ ...payData, bank_account_id: v })}>
                      <SelectTrigger data-testid="pay-bank-account-select"><SelectValue placeholder="Main bank (default)" /></SelectTrigger>
                      <SelectContent>{payBanks.filter(a => !payData.branch_id || !(a.branch_ids || []).length || a.branch_ids.includes(payData.branch_id)).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">Posts to that bank's ledger account (if mapped)</p>
                  </div>
                )}
                <div><Label>Paid on Date <span className="text-[10px] text-muted-foreground font-normal">(drives the expense month)</span></Label><Input type="date" value={payData.date} onChange={(e) => setPayData({ ...payData, date: e.target.value })} data-testid="pay-date" /></div>
              </div>
              <div><Label>Notes</Label><Input value={payData.notes} onChange={(e) => setPayData({ ...payData, notes: e.target.value })} placeholder="Optional" /></div>
              {salaryAlerts.length > 0 && (
                <div className="space-y-1.5" data-testid="salary-alerts">
                  {salaryAlerts.map((a, i) => (
                    <div key={i} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[12px] border ${
                      a.level === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : a.level === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid={`salary-alert-${i}`}>
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span>{a.message}</span>
                    </div>
                  ))}
                </div>
              )}
              {payData.payment_type === 'salary' && (
                <SalaryDeductionCard
                  deductions={deductions}
                  cur={cur()}
                  onApply={(rec) => {
                    const sal = parseFloat(payingEmp?.salary) || 0;
                    const net = deductions?.remaining_after_deduction != null
                      ? deductions.remaining_after_deduction
                      : Math.max(0, (parseFloat(payData.amount) || sal) - rec);
                    setPayData(d => ({ ...d, amount: String(Math.max(0, Number(net) || 0)) }));
                    toast.success(`Deduction applied — paying ${cur()} ${(net || 0).toFixed(2)} after ${cur()} ${rec.toFixed(2)} deduction`);
                  }}
                />
              )}
              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-salary" className="rounded-full">Record Payment</Button>
                <Button type="button" variant="outline" onClick={() => setShowPayDialog(false)} className="rounded-full">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Per-employee Salary Status (month-by-month, full year) */}
        <SalaryStatusDialog
          open={!!statusEmp}
          onOpenChange={(o) => { if (!o) setStatusEmp(null); }}
          entity={statusEmp ? { id: statusEmp.id, name: statusEmp.name, type: 'employee' } : null}
        />

        {/* Mid-month Branch Transfer (pro-rata labour cost) */}
        <Dialog open={!!transferEmp} onOpenChange={(o) => { if (!o) setTransferEmp(null); }}>
          <DialogContent data-testid="transfer-branch-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Transfer Branch — {transferEmp?.name}</DialogTitle></DialogHeader>
            {transferEmp && (
              <div className="space-y-4">
                <p className="text-[12px] text-muted-foreground">Currently at <strong>{branchName(transferEmp.branch_id)}</strong>. Payroll splits that month's salary across branches by days at each branch.</p>
                <div>
                  <Label>Move to Branch *</Label>
                  <Select value={transferForm.to_branch_id} onValueChange={(v) => setTransferForm({ ...transferForm, to_branch_id: v })}>
                    <SelectTrigger data-testid="transfer-to-branch"><SelectValue placeholder="Select destination branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.filter((b) => b.id !== transferEmp.branch_id).map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Effective Date *</Label><Input type="date" value={transferForm.effective_date} data-testid="transfer-effective-date" onChange={(e) => setTransferForm({ ...transferForm, effective_date: e.target.value })} /><p className="text-[10px] text-muted-foreground mt-1">Day the move takes effect. Payroll pro-rates from this date.</p></div>
                  <div><Label>Reason</Label><Input value={transferForm.reason} data-testid="transfer-reason" onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })} placeholder="e.g. Branch reassignment" /></div>
                </div>
                {transferHistory.length > 0 && (
                  <div className="rounded-xl border border-border p-3 space-y-1.5 max-h-40 overflow-y-auto" data-testid="transfer-history-list">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Transfer History</p>
                    {transferHistory.map((h) => (
                      <div key={h.id} className="text-[12px] flex items-center justify-between gap-2">
                        <span>{branchName(h.old_branch_id)} <ArrowLeftRight size={11} className="inline mx-1 text-sky-500" /> {branchName(h.new_branch_id)}</span>
                        <span className="text-muted-foreground">{String(h.effective_date).slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Button onClick={submitTransfer} disabled={transferSaving} className="w-full rounded-full" data-testid="transfer-branch-submit">{transferSaving ? 'Saving...' : 'Record Transfer'}</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Leave Dialog */}
        <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
          <DialogContent data-testid="leave-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Record Leave - {leaveEmp?.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleLeave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Leave Type *</Label>
                  <Select value={leaveData.leave_type} onValueChange={(v) => setLeaveData({ ...leaveData, leave_type: v })}>
                    <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Start Date *</Label><Input type="date" value={leaveData.start_date} data-testid="leave-start" onChange={(e) => { const s = e.target.value; const days = s && leaveData.end_date ? Math.max(1, Math.round((new Date(leaveData.end_date) - new Date(s)) / 86400000) + 1) : ''; setLeaveData({ ...leaveData, start_date: s, days }); }} required /></div>
                <div><Label>End Date *</Label><Input type="date" value={leaveData.end_date} data-testid="leave-end" onChange={(e) => { const en = e.target.value; const days = leaveData.start_date && en ? Math.max(1, Math.round((new Date(en) - new Date(leaveData.start_date)) / 86400000) + 1) : ''; setLeaveData({ ...leaveData, end_date: en, days }); }} required /></div>
                <div><Label>Days</Label><Input type="number" value={leaveData.days} data-testid="leave-days" readOnly className="bg-muted/50 font-bold" /></div>
              </div>
              <div><Label>Reason</Label><Textarea value={leaveData.reason} onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })} placeholder="Optional reason" /></div>
              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-leave" className="rounded-full">Record Leave</Button>
                <Button type="button" variant="outline" onClick={() => setShowLeaveDialog(false)} className="rounded-full">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Employee Summary Dialog */}
        <Dialog open={showSummary} onOpenChange={setShowSummary}>
          <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto" data-testid="summary-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Payment Summary - {empSummary?.employee?.name} {(() => { const jt = jobTitles.find(j => j.id === empSummary?.employee?.job_title_id); return jt ? <Badge variant="outline" className="ml-2 capitalize">{jt.title}</Badge> : ''; })()}</DialogTitle></DialogHeader>
            {empSummary && (
              <Tabs defaultValue="payments">
                <TabsList className="mb-4"><TabsTrigger value="payments">Payments</TabsTrigger><TabsTrigger value="loan">Loan</TabsTrigger><TabsTrigger value="leave">Leave</TabsTrigger><TabsTrigger value="deductions">Deductions</TabsTrigger><TabsTrigger value="salary_history">Salary History</TabsTrigger><TabsTrigger value="docs">Documents</TabsTrigger><TabsTrigger value="pin" data-testid="pin-tab">Portal PIN</TabsTrigger></TabsList>

                <TabsContent value="payments" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="p-3 bg-secondary/50 rounded-lg"><div className="text-xs text-muted-foreground">Monthly Salary</div><div className="text-xl font-bold"> {cur()} {empSummary.employee.salary.toFixed(2)}</div></div>
                    <div className="p-3 bg-success/10 rounded-lg"><div className="text-xs text-muted-foreground">Total Paid</div><div className="text-xl font-bold text-success"> {cur()} {empSummary.total_all_time.toFixed(2)}</div></div>
                    <div className="p-3 bg-warning/10 rounded-lg"><div className="text-xs text-muted-foreground">Loan Balance</div><div className="text-xl font-bold text-warning"> {cur()} {empSummary.loan.balance.toFixed(2)}</div></div>
                    {(empSummary?.old_salary_balance || 0) > 0 && <div className="p-3 bg-error/10 rounded-lg"><div className="text-xs text-muted-foreground">Old Balance Due</div><div className="text-xl font-bold text-error"> {cur()} {empSummary.old_salary_balance.toFixed(2)}</div></div>}
                  </div>
                  {empSummary.monthly_summary.slice(0, monthsVisible).map((m) => (
                    <Card key={m.period} className={`border-border ${m.salary_status === 'partial' ? 'border-l-4 border-l-amber-400' : m.salary_status === 'paid' ? 'border-l-4 border-l-emerald-400' : ''}`}>
                      <CardHeader className="py-3">
                        <div className="flex justify-between items-center flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <CardTitle className="font-outfit text-base">{m.period}</CardTitle>
                            {/* iter838 — Partial payment status pill on the
                                Employee → Summary → Monthly card header. */}
                            {m.salary_status === 'paid' && (
                              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]" data-testid={`emp-summary-status-paid-${m.period}`}>Paid ✓</Badge>
                            )}
                            {m.salary_status === 'partial' && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]" data-testid={`emp-summary-status-partial-${m.period}`} title={`Remaining ${cur()} ${(m.salary_remaining || 0).toFixed(2)}`}>Partial · {cur()} {(m.salary_remaining || 0).toFixed(2)} left</Badge>
                            )}
                            {m.salary_status === 'unpaid' && (
                              <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px]" data-testid={`emp-summary-status-unpaid-${m.period}`}>Unpaid</Badge>
                            )}
                          </div>
                          <div className="flex gap-3 text-sm items-center">
                            <span>Paid: <span className="font-bold text-success"> {cur()} {m.salary_paid.toFixed(2)}</span></span>
                            <span>Balance: <span className={`font-bold ${m.balance > 0 ? 'text-error' : 'text-success'}`}> {cur()} {m.balance.toFixed(2)}</span></span>
                            {/* iter839 — Pay Remaining quick action.
                                Prefills the pay dialog with the exact
                                remaining amount for this period. */}
                            {m.salary_status === 'partial' && (m.salary_remaining || 0) > 0.005 && (
                              <Button
                                size="sm"
                                className="h-7 text-[11px] bg-amber-500 hover:bg-amber-600 text-white px-2"
                                onClick={() => {
                                  setPayingEmp(empSummary.employee);
                                  setPayData({
                                    payment_type: 'salary',
                                    amount: String((m.salary_remaining || 0).toFixed(2)),
                                    payment_mode: 'cash',
                                    branch_id: empSummary.employee?.branch_id || '',
                                    period: m.period,
                                    date: localToday(),
                                    notes: `Remaining balance for ${m.period}`,
                                  });
                                  setShowPayDialog(true);
                                }}
                                data-testid={`pay-remaining-${m.period}`}
                                title={`Pay the remaining ${cur()} ${(m.salary_remaining || 0).toFixed(2)}`}
                              >
                                Pay Remaining
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex gap-2 flex-wrap mb-3">
                          {m.salary_paid > 0 && <Badge className="bg-success/20 text-success">Salary: ${m.salary_paid.toFixed(2)}</Badge>}
                          {m.advance > 0 && <Badge className="bg-info/20 text-info">Advance: ${m.advance.toFixed(2)}</Badge>}
                          {m.loan_repayment > 0 && <Badge className="bg-primary/20 text-primary">Loan Repay: ${m.loan_repayment.toFixed(2)}</Badge>}
                          {m.overtime > 0 && <Badge className="bg-primary/20 text-primary">Overtime: ${m.overtime.toFixed(2)}</Badge>}
                          {m.tickets > 0 && <Badge className="bg-warning/20 text-warning">Tickets: ${m.tickets.toFixed(2)}</Badge>}
                          {m.id_card > 0 && <Badge className="bg-error/20 text-error">ID Card: ${m.id_card.toFixed(2)}</Badge>}
                        </div>
                        <div className="space-y-1">
                          {m.payments.map(p => (
                            <div key={p.id} className="flex justify-between items-center text-xs p-2 bg-secondary/30 rounded">
                              <span>{format(new Date(p.date), 'MMM dd')} - <span className="capitalize font-medium">{(p.payment_type || 'payment').replace('_', ' ')}</span> ({p.payment_mode || '-'}){p.notes ? ` - ${p.notes}` : ''}</span>
                              <div className="flex items-center gap-2">
                                <span className="font-bold"> {cur()} {p.amount.toFixed(2)}</span>
                                <select
                                  value={p.funding_branch_id || ''}
                                  onChange={async (e) => {
                                    try {
                                      await api.patch(`/salary-payments/${p.id}/funding-branch`, { funding_branch_id: e.target.value || '' });
                                      toast.success(e.target.value ? `Funding branch set to ${branchName(e.target.value)}` : 'Funding branch cleared');
                                      viewSummary(empSummary.employee); fetchData();
                                    } catch (err) {
                                      toast.error(`Update failed (HTTP ${err.response?.status ?? 'network'})`);
                                    }
                                  }}
                                  className="h-6 text-[10px] rounded border border-border bg-background px-1"
                                  title="Funding branch (who paid)"
                                  data-testid={`funding-branch-${p.id}`}
                                >
                                  <option value="">Funder: own</option>
                                  {branches.map((b) => <option key={b.id} value={b.id}>Funder: {b.name}</option>)}
                                </select>
                                <Button size="sm" variant="ghost" onClick={() => setEditPay({ id: p.id, amount: String(p.amount), date: String(p.date).slice(0,10), payment_mode: p.payment_mode || 'cash', notes: p.notes || '' })} className="h-6 w-6 p-0" title="Correct Payment" data-testid={`edit-pay-${p.id}`}><Edit size={12} /></Button>
                                <Button size="sm" variant="ghost" onClick={() => downloadPayslip(p.id)} className="h-6 w-6 p-0" title="Download Payslip"><FileText size={12} /></Button>
                                <Button size="sm" variant="ghost" onClick={async () => { const _p = askCancel('Delete this payment? The linked expense will also be removed.', !!p.journal_entry_id); if (_p) { try { const _r = await api.delete(`/salary-payments/${p.id}`, { params: _p }); toast.success(_r.data?.message || 'Payment deleted'); viewSummary(empSummary.employee); fetchData(); } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); } } }} className="h-6 w-6 p-0 text-error" title="Delete Payment" data-testid={`del-pay-${p.id}`}><Trash2 size={12} /></Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {empSummary.monthly_summary.length > monthsVisible && (
                    <div className="text-center">
                      <Button variant="outline" className="rounded-xl" onClick={() => setMonthsVisible(c => c + 6)} data-testid="load-more-emp-months-btn">
                        Load More ({Math.min(monthsVisible, empSummary.monthly_summary.length)} of {empSummary.monthly_summary.length})
                      </Button>
                      <Button variant="ghost" className="rounded-xl ml-2" onClick={() => setMonthsVisible(empSummary.monthly_summary.length)} data-testid="show-all-emp-months-btn">
                        Show All
                      </Button>
                    </div>
                  )}
                  {empSummary.monthly_summary.length === 0 && <p className="text-center text-muted-foreground py-4">No payments recorded yet</p>}
                </TabsContent>

                <TabsContent value="loan" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="p-3 bg-info/10 rounded-lg"><div className="text-xs text-muted-foreground">Total Advance Taken</div><div className="text-xl font-bold text-info"> {cur()} {empSummary.loan.total_advance.toFixed(2)}</div></div>
                    <div className="p-3 bg-success/10 rounded-lg"><div className="text-xs text-muted-foreground">Total Repaid</div><div className="text-xl font-bold text-success"> {cur()} {empSummary.loan.total_repaid.toFixed(2)}</div></div>
                    <div className="p-3 bg-warning/10 rounded-lg border border-warning/30"><div className="text-xs text-muted-foreground">Outstanding Balance</div><div className="text-xl font-bold text-warning"> {cur()} {empSummary.loan.balance.toFixed(2)}</div></div>
                  </div>
                  <p className="text-sm text-muted-foreground">Advance/loan payments and repayments are tracked automatically. Use &quot;Advance / Loan&quot; type to give advance, and &quot;Loan Repayment&quot; to deduct from balance.</p>

                  {/* iter860 — Deduction Ledger: every loan installment linked to its source salary payment. */}
                  {empSummary?.deduction_ledger?.length > 0 && (
                    <div data-testid="deduction-ledger">
                      <div className="flex items-center gap-2 mb-2 mt-4">
                        <p className="text-sm font-semibold">Deduction Ledger</p>
                        <Badge variant="outline" className="text-[10px]">{empSummary.deduction_ledger.length} entr{empSummary.deduction_ledger.length === 1 ? 'y' : 'ies'}</Badge>
                        {/* iter861 — Export buttons */}
                        <div className="ml-auto flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px]"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/deduction-ledger/employee/${empSummary.employee.id}.csv`, { responseType: 'blob' });
                                const url = URL.createObjectURL(res.data);
                                const a = document.createElement('a');
                                a.href = url; a.download = `deduction-ledger-${empSummary.employee.name}.csv`; a.click();
                                URL.revokeObjectURL(url);
                              } catch (e) { toast.error('Export failed'); }
                            }}
                            data-testid="ledger-export-csv"
                            title="Download ledger as CSV (Excel-friendly)"
                          >
                            CSV
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px]"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/deduction-ledger/employee/${empSummary.employee.id}.pdf`, { responseType: 'blob' });
                                const url = URL.createObjectURL(res.data);
                                const a = document.createElement('a');
                                a.href = url; a.download = `deduction-ledger-${empSummary.employee.name}.pdf`; a.click();
                                URL.revokeObjectURL(url);
                              } catch (e) { toast.error('Export failed'); }
                            }}
                            data-testid="ledger-export-pdf"
                            title="Download ledger as PDF"
                          >
                            PDF
                          </Button>
                        </div>
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-2">Each row shows a repayment against a loan. Auto entries were deducted straight from a salary payment — click the pill to jump to the source.</p>
                      <div className="space-y-1">
                        {empSummary.deduction_ledger.map(d => (
                          <div key={d.id} className="flex justify-between items-center text-xs p-2 bg-secondary/40 rounded-lg border border-border" data-testid={`deduction-ledger-row-${d.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-[10px] text-muted-foreground">{d.date ? String(d.date).slice(0,10) : '—'}</span>
                              <Badge variant="secondary" className="capitalize">{d.loan_type || 'loan'}</Badge>
                              {d.auto ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]" data-testid={`ledger-auto-${d.id}`}>Auto</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">Manual</Badge>
                              )}
                              {d.period && <span className="text-[10px] text-muted-foreground">Period {d.period}</span>}
                              {d.source_salary_payment_id && (
                                <button
                                  type="button"
                                  className="text-[10px] text-primary underline decoration-dotted hover:decoration-solid"
                                  onClick={() => {
                                    const el = document.querySelector(`[data-testid="edit-pay-${d.source_salary_payment_id}"]`);
                                    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-primary'); setTimeout(() => el.classList.remove('ring-2', 'ring-primary'), 1600); }
                                  }}
                                  data-testid={`ledger-source-${d.id}`}
                                  title="Reveal source salary payment in the Payments tab"
                                >
                                  from salary #{String(d.source_salary_payment_id).slice(0, 6)}…
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-right">
                              <span className="font-bold text-error">-{cur()} {Number(d.amount || 0).toFixed(2)}</span>
                              <span className="text-[10px] text-muted-foreground">Bal after {cur()} {Number(d.remaining_balance_after || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="leave" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="p-3 bg-success/10 rounded-lg"><div className="text-xs text-muted-foreground">Annual Leave</div><div className="text-lg font-bold">{empSummary.leave.annual_used} / {empSummary.employee.annual_leave_entitled} used</div><div className="text-xs text-success">{empSummary.leave.annual_remaining} remaining</div></div>
                    <div className="p-3 bg-error/10 rounded-lg"><div className="text-xs text-muted-foreground">Sick Leave</div><div className="text-lg font-bold">{empSummary.leave.sick_used} / {empSummary.employee.sick_leave_entitled} used</div><div className="text-xs text-error">{empSummary.leave.sick_remaining} remaining</div></div>
                    <div className="p-3 bg-secondary rounded-lg"><div className="text-xs text-muted-foreground">Unpaid Leave</div><div className="text-lg font-bold">{empSummary.leave.unpaid_used} days</div></div>
                  </div>
                </TabsContent>

                <TabsContent value="deductions" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="p-3 bg-error/10 rounded-xl"><div className="text-xs text-muted-foreground">Total Deductions</div><div className="text-lg font-bold text-error">{cur()} {(empSummary?.total_deductions || 0).toFixed(2)}</div></div>
                    <div className="p-3 bg-orange-50 rounded-xl"><div className="text-xs text-muted-foreground">Total Fines</div><div className="text-lg font-bold text-orange-600">{cur()} {(empSummary?.total_fines || 0).toFixed(2)}</div></div>
                    <div className="p-3 bg-warning/10 rounded-xl"><div className="text-xs text-muted-foreground">Unpaid Fines</div><div className="text-lg font-bold text-warning">{cur()} {(empSummary?.unpaid_fines || 0).toFixed(2)}</div></div>
                  </div>
                  {empSummary?.deductions?.length > 0 && (
                    <div><p className="text-sm font-medium mb-2">Salary Deductions</p>
                    <div className="space-y-1">{empSummary.deductions.map(d => (
                      <div key={d.id} className="flex justify-between items-center text-xs p-2 bg-error/5 rounded-lg border border-error/20"><span><Badge variant="secondary" className="capitalize mr-2">{d.type}</Badge>{d.reason} ({d.period})</span><span className="font-bold text-error">{cur()} {d.amount.toFixed(2)}</span></div>
                    ))}</div></div>
                  )}
                  {empSummary?.fines?.length > 0 && (
                    <div><p className="text-sm font-medium mb-2">Fines Charged</p>
                    <div className="space-y-1">{empSummary.fines.map(f => (
                      <div key={f.id} className="flex justify-between items-center text-xs p-2 bg-orange-50 rounded-lg border border-orange-200"><span><Badge variant="secondary" className="capitalize mr-2">{f.type}</Badge>{f.department}: {f.description}</span><div className="text-right"><span className="font-bold">{cur()} {f.amount.toFixed(2)}</span>{f.status !== 'paid' && <Badge className="ml-2 bg-warning/20 text-warning">{f.status}</Badge>}</div></div>
                    ))}</div></div>
                  )}
                  {(!empSummary?.deductions?.length && !empSummary?.fines?.length) && <p className="text-center text-muted-foreground py-4">No deductions or fines</p>}
                </TabsContent>

                <TabsContent value="salary_history" className="space-y-4">
                  <div className="flex gap-2 items-end p-3 bg-muted/50 rounded-xl border">
                    <div className="w-28"><Label className="text-xs">New Salary</Label><Input type="number" step="0.01" value={newIncrement.new_salary} onChange={(e) => setNewIncrement({ ...newIncrement, new_salary: e.target.value })} className="h-8" placeholder="SR" data-testid="whatif-new-salary-input" /></div>
                    <div className="w-36"><Label className="text-xs">Effective Date</Label><Input type="date" value={newIncrement.effective_date} onChange={(e) => setNewIncrement({ ...newIncrement, effective_date: e.target.value })} className="h-8" data-testid="whatif-effective-date-input" /></div>
                    <div className="flex-1"><Label className="text-xs">Reason</Label><Input value={newIncrement.reason} onChange={(e) => setNewIncrement({ ...newIncrement, reason: e.target.value })} className="h-8" placeholder="Annual increment" /></div>
                    <Button size="sm" variant="outline" className="h-8 rounded-xl" data-testid="whatif-preview-btn" disabled={whatIfLoading} onClick={async () => {
                      if (!newIncrement.new_salary || !newIncrement.effective_date || !empSummary?.employee?.id) {
                        toast.error('Enter both new salary and effective date first');
                        return;
                      }
                      try {
                        setWhatIfLoading(true);
                        const eff = new Date(newIncrement.effective_date);
                        const { data } = await api.post(`/employees/${empSummary.employee.id}/salary-what-if`, {
                          new_salary: parseFloat(newIncrement.new_salary),
                          effective_date: newIncrement.effective_date,
                          year: eff.getFullYear(),
                          month: eff.getMonth() + 1,
                        });
                        setWhatIfPreview(data);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'What-If preview failed');
                        setWhatIfPreview(null);
                      } finally {
                        setWhatIfLoading(false);
                      }
                    }}>
                      {whatIfLoading ? 'Loading…' : 'Preview raise cost'}
                    </Button>
                    <Button size="sm" className="h-8 rounded-xl" onClick={async () => {
                      if (!newIncrement.new_salary || !newIncrement.effective_date || !empSummary?.employee?.id) return;
                      try {
                        await api.post('/salary-history', { employee_id: empSummary.employee.id, new_salary: newIncrement.new_salary, effective_date: new Date(newIncrement.effective_date).toISOString(), reason: newIncrement.reason });
                        toast.success('Salary updated'); setNewIncrement({ new_salary: '', effective_date: '', reason: '' });
                        setWhatIfPreview(null);
                        const h = await api.get(`/salary-history/${empSummary.employee.id}`); setSalaryHistory(h.data); fetchData();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    }}>Update Salary</Button>
                  </div>
                  {whatIfPreview && (
                    <div className="p-3 rounded-xl border bg-sky-50 dark:bg-sky-950/30 space-y-2" data-testid="whatif-preview-panel">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-sky-700 dark:text-sky-300">
                          What-If preview — {whatIfPreview.year}/{String(whatIfPreview.month).padStart(2, '0')}
                        </p>
                        <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setWhatIfPreview(null)} data-testid="whatif-close-btn">✕ Dismiss</button>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="p-2 rounded bg-background/60">
                          <div className="text-[10px] uppercase text-muted-foreground">Old salary</div>
                          <div className="font-semibold" data-testid="whatif-old">{cur()} {(whatIfPreview.hypothetical?.old_salary || 0).toFixed(2)}</div>
                        </div>
                        <div className="p-2 rounded bg-background/60">
                          <div className="text-[10px] uppercase text-muted-foreground">New salary</div>
                          <div className="font-semibold" data-testid="whatif-new">{cur()} {(whatIfPreview.hypothetical?.new_salary || 0).toFixed(2)}</div>
                        </div>
                        <div className="p-2 rounded bg-background/60">
                          <div className="text-[10px] uppercase text-muted-foreground">Δ per month</div>
                          <div className={`font-semibold ${(whatIfPreview.hypothetical?.delta_per_month || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="whatif-delta">
                            {(whatIfPreview.hypothetical?.delta_per_month || 0) >= 0 ? '+' : ''}{cur()} {(whatIfPreview.hypothetical?.delta_per_month || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <div className="pt-1 border-t border-sky-200/50">
                        <div className="text-[11px] text-muted-foreground">Pro-rated total for this month ({whatIfPreview.days_in_month} days)</div>
                        <div className="text-lg font-bold" data-testid="whatif-prorated-total">{cur()} {(whatIfPreview.total || 0).toFixed(2)}</div>
                        {Array.isArray(whatIfPreview.tiers) && whatIfPreview.tiers.length > 1 && (
                          <div className="mt-1 space-y-0.5">
                            {whatIfPreview.tiers.map((t, i) => (
                              <div key={i} className="text-[11px] text-muted-foreground flex justify-between">
                                <span>{t.days} day{t.days === 1 ? '' : 's'} @ {cur()} {Number(t.salary || 0).toFixed(2)}</span>
                                <span>= {cur()} {(Number(t.salary || 0) * (t.days / whatIfPreview.days_in_month)).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-2 italic">This preview is not saved. Press <b>Update Salary</b> to commit the raise.</p>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    {salaryHistory.map(h => (
                      <div key={h.id} className="flex justify-between items-center p-3 rounded-xl border bg-muted/50">
                        <div><span className="text-sm font-medium">{cur()} {h.old_salary.toFixed(2)} → {cur()} {h.new_salary.toFixed(2)}</span><div className="text-xs text-muted-foreground mt-1">{h.reason || 'No reason'} | {new Date(h.effective_date).toLocaleDateString()}</div></div>
                        <Badge className={h.new_salary > h.old_salary ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}>{h.new_salary > h.old_salary ? '+' : ''}{(h.new_salary - h.old_salary).toFixed(2)}</Badge>
                      </div>
                    ))}
                    {salaryHistory.length === 0 && <p className="text-center text-muted-foreground py-4">No salary history</p>}
                  </div>
                  {empSummary?.employee?.id && (
                    <div className="pt-3 mt-1 border-t border-border">
                      <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><History size={13} /> Account Salary Changes (audit log)</p>
                      <SalaryChangesPanel entityType="employee" entityId={empSummary.employee.id} />
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="docs" className="space-y-4">
                  <div className="flex gap-2 items-end p-3 bg-muted/50 rounded-xl border">
                    <div className="w-36"><Label className="text-xs">Type</Label>
                      <Select value={newDoc.document_type} onValueChange={(v) => setNewDoc({ ...newDoc, document_type: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="passport">Passport</SelectItem><SelectItem value="visa">Visa</SelectItem><SelectItem value="labor_card">Labor Card</SelectItem><SelectItem value="emirates_id">Emirates ID</SelectItem><SelectItem value="health_card">Health Card</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1"><Label className="text-xs">Number</Label><Input value={newDoc.document_number} onChange={(e) => setNewDoc({ ...newDoc, document_number: e.target.value })} className="h-8" placeholder="Doc number" /></div>
                    <div className="w-36"><Label className="text-xs">Expiry</Label><Input type="date" value={newDoc.expiry_date} onChange={(e) => setNewDoc({ ...newDoc, expiry_date: e.target.value })} className="h-8" /></div>
                    <Button size="sm" onClick={handleAddEmpDoc} className="h-8 rounded-xl">Add</Button>
                  </div>
                  <div className="space-y-2">
                    {empDocs.map(d => (
                      <div key={d.id} className={`flex justify-between items-center p-3 rounded-xl border ${d.days_until_expiry != null && d.days_until_expiry <= 30 ? 'bg-error/5 border-error/30' : 'bg-muted/50'}`}>
                        <div>
                          <Badge variant="secondary" className="capitalize mr-2">{(d.document_type || '').replace('_', ' ')}</Badge>
                          <span className="text-sm">{d.document_number || '-'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {d.expiry_date && <span className="text-xs">{new Date(d.expiry_date).toLocaleDateString()}</span>}
                          {d.days_until_expiry != null && (
                            <Badge className={d.days_until_expiry < 0 ? 'bg-error/20 text-error' : d.days_until_expiry <= 30 ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'}>
                              {d.days_until_expiry < 0 ? `${Math.abs(d.days_until_expiry)}d expired` : `${d.days_until_expiry}d left`}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {empDocs.length === 0 && <p className="text-center text-muted-foreground py-4">No documents added</p>}
                  </div>
                </TabsContent>

                <TabsContent value="pin" className="space-y-4">
                  <Card className="border-violet-200 bg-violet-50/30">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2.5 bg-violet-100 rounded-xl"><KeyRound size={20} className="text-violet-600" /></div>
                        <div>
                          <h3 className="font-semibold text-sm">Portal Access PIN</h3>
                          <p className="text-xs text-muted-foreground">Used for Cashier, Waiter, and Kitchen Display login</p>
                        </div>
                      </div>
                      {empSummary?.employee?.cashier_pin ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-violet-200">
                            <p className="text-xs text-muted-foreground">Current PIN:</p>
                            <span className="text-3xl font-mono font-bold tracking-[0.3em] text-violet-700" data-testid="current-pin-display">
                              {empSummary.employee.cashier_pin}
                            </span>
                            <Button size="sm" variant="ghost" className="h-8 text-violet-600" onClick={() => copyPin(empSummary.employee.cashier_pin)} data-testid="copy-pin-btn">
                              <Copy size={14} className="mr-1" /> Copy
                            </Button>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50"
                              onClick={() => handleGeneratePin(empSummary.employee.id, empSummary.employee.name)} disabled={pinLoading === empSummary.employee.id}
                              data-testid="regenerate-pin-btn">
                              {pinLoading === empSummary.employee.id ? <RefreshCw size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                              Regenerate PIN
                            </Button>
                            <Button size="sm" variant="outline" className="text-error border-error/30 hover:bg-error/10"
                              onClick={() => handleRevokePin(empSummary.employee.id, empSummary.employee.name)} disabled={pinLoading === empSummary.employee.id}
                              data-testid="revoke-pin-btn">
                              <Trash2 size={12} className="mr-1" /> Revoke PIN
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <KeyRound size={32} className="mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground mb-3">No PIN assigned. Generate one to enable portal access.</p>
                          <Button className="bg-violet-600 hover:bg-violet-700"
                            onClick={() => handleGeneratePin(empSummary.employee.id, empSummary.employee.name)} disabled={pinLoading === empSummary.employee.id}
                            data-testid="generate-pin-summary-btn">
                            {pinLoading === empSummary.employee.id ? <RefreshCw size={14} className="animate-spin mr-1" /> : <KeyRound size={14} className="mr-1" />}
                            Generate PIN
                          </Button>
                        </div>
                      )}
                      <div className="mt-4 p-3 bg-muted/50 rounded-lg border text-xs text-muted-foreground space-y-1">
                        <p><strong>How PINs work:</strong></p>
                        <p>Employees use this 4-digit PIN to log into Cashier POS, Waiter, and Kitchen Display portals.</p>
                        <p>Each PIN is unique. Regenerating creates a new one and invalidates the old PIN.</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Job Title Manager Dialog */}
        <Dialog open={showJobTitleManager} onOpenChange={(v) => { setShowJobTitleManager(v); if (!v) setEditingJT(null); }}>
          <DialogContent className="max-w-2xl" data-testid="job-title-manager-dialog">
            <DialogHeader><DialogTitle className="font-outfit flex items-center justify-between gap-2">
              <span>Manage Job Titles</span>
              <IndustryPresetPicker onSeeded={async () => { const j = await api.get('/job-titles'); setJobTitles(j.data); }} />
            </DialogTitle></DialogHeader>
            <div className="space-y-4 max-h-[70dvh] overflow-y-auto">
              {/* Add/Edit Form */}
              <div className="space-y-3 p-3 border rounded-xl bg-muted/50">
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 items-end">
                  <div><Label className="text-xs">Title *</Label><Input value={editingJT ? editingJT.title : newJobTitle.title} onChange={(e) => editingJT ? setEditingJT({ ...editingJT, title: e.target.value }) : setNewJobTitle({ ...newJobTitle, title: e.target.value })} placeholder="e.g. Chef" className="h-9" data-testid="new-jt-title" /></div>
                  <div><Label className="text-xs">Department</Label><Input value={editingJT ? editingJT.department : newJobTitle.department} onChange={(e) => editingJT ? setEditingJT({ ...editingJT, department: e.target.value }) : setNewJobTitle({ ...newJobTitle, department: e.target.value })} placeholder="e.g. Kitchen" className="h-9" /></div>
                  <div><Label className="text-xs">Min Salary</Label><Input type="number" value={editingJT ? editingJT.min_salary : newJobTitle.min_salary} onChange={(e) => editingJT ? setEditingJT({ ...editingJT, min_salary: e.target.value }) : setNewJobTitle({ ...newJobTitle, min_salary: e.target.value })} placeholder="SR" className="h-9" /></div>
                  <div><Label className="text-xs">Max Salary</Label><Input type="number" value={editingJT ? editingJT.max_salary : newJobTitle.max_salary} onChange={(e) => editingJT ? setEditingJT({ ...editingJT, max_salary: e.target.value }) : setNewJobTitle({ ...newJobTitle, max_salary: e.target.value })} placeholder="SR" className="h-9" /></div>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Permissions (Pages this role can access)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_PERMISSIONS.map(p => {
                      const perms = editingJT ? (editingJT.permissions || []) : (newJobTitle.permissions || []);
                      const active = perms.includes(p.key);
                      return (
                        <button key={p.key} type="button" data-testid={`perm-toggle-${p.key}`}
                          className={`px-2.5 py-1 text-xs rounded-lg border transition-all ${active ? 'bg-orange-100 border-orange-400 text-orange-700 font-medium' : 'bg-white border-border text-muted-foreground hover:border-border'}`}
                          onClick={() => {
                            const newPerms = active ? perms.filter(x => x !== p.key) : [...perms, p.key];
                            editingJT ? setEditingJT({ ...editingJT, permissions: newPerms }) : setNewJobTitle({ ...newJobTitle, permissions: newPerms });
                          }}>
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2">
                  {editingJT ? (
                    <>
                      <Button size="sm" className="h-8 rounded-xl" data-testid="save-jt-btn" onClick={async () => {
                        try {
                          await api.put(`/job-titles/${editingJT.id}`, { ...editingJT, min_salary: parseFloat(editingJT.min_salary) || 0, max_salary: parseFloat(editingJT.max_salary) || 0 });
                          toast.success('Job title updated');
                          setEditingJT(null);
                          const r = await api.get('/job-titles'); setJobTitles(r.data);
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }}><Edit size={14} className="mr-1" />Save Changes</Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-xl" onClick={() => setEditingJT(null)}>Cancel</Button>
                    </>
                  ) : (
                    <Button size="sm" className="h-8 rounded-xl" data-testid="add-jt-btn" onClick={async () => {
                      if (!newJobTitle.title) { toast.error('Title required'); return; }
                      try {
                        await api.post('/job-titles', { ...newJobTitle, min_salary: parseFloat(newJobTitle.min_salary) || 0, max_salary: parseFloat(newJobTitle.max_salary) || 0 });
                        toast.success('Job title added');
                        setNewJobTitle({ title: '', department: '', min_salary: '', max_salary: '', permissions: [] });
                        const r = await api.get('/job-titles'); setJobTitles(r.data);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    }}><Plus size={14} className="mr-1" />Add</Button>
                  )}
                </div>
              </div>
              {/* Table */}
              <div className="border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-muted/50 border-b">
                      <th className="text-left p-2 font-medium">Title</th>
                      <th className="text-left p-2 font-medium">Department</th>
                      <th className="text-right p-2 font-medium">Salary Range</th>
                      <th className="text-left p-2 font-medium">Permissions</th>
                      <th className="text-right p-2 font-medium">Actions</th>
                    </tr></thead>
                    <tbody>
                      {jobTitles.map(jt => (
                        <tr key={jt.id} className="border-b hover:bg-muted/50" data-testid={`jt-row-${jt.id}`}>
                          <td className="p-2 font-medium">{jt.title}</td>
                          <td className="p-2 text-muted-foreground">{jt.department || '-'}</td>
                          <td className="p-2 text-right">{cur()} {jt.min_salary?.toFixed(0) || 0} - {jt.max_salary?.toFixed(0) || 0}</td>
                          <td className="p-2">
                            <div className="flex flex-wrap gap-1">
                              {(jt.permissions || []).length > 0 ?
                                (jt.permissions || []).slice(0, 3).map(p => <Badge key={p} variant="outline" className="text-[10px] py-0">{p}</Badge>)
                                : <span className="text-xs text-muted-foreground">None</span>}
                              {(jt.permissions || []).length > 3 && <Badge variant="outline" className="text-[10px] py-0">+{jt.permissions.length - 3}</Badge>}
                            </div>
                          </td>
                          <td className="p-2 text-right flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" className="h-7" data-testid={`edit-jt-${jt.id}`} onClick={() => setEditingJT({ ...jt, min_salary: jt.min_salary || 0, max_salary: jt.max_salary || 0 })}><Edit size={12} /></Button>
                            <Button size="sm" variant="ghost" className="h-7 text-error" onClick={async () => {
                              if (window.confirm(`Delete "${jt.title}"?`)) {
                                await api.delete(`/job-titles/${jt.id}`);
                                const r = await api.get('/job-titles'); setJobTitles(r.data);
                                toast.success('Deleted');
                              }
                            }}><Trash2 size={12} /></Button>
                          </td>
                        </tr>
                      ))}
                      {jobTitles.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No job titles</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Resignation / Termination Dialog */}
        <Dialog open={!!resignDialog} onOpenChange={(v) => !v && setResignDialog(null)}>
          <DialogContent className="max-w-md" data-testid="resign-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Employee Exit Process</DialogTitle></DialogHeader>
            {resignDialog && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Processing exit for <strong>{resignDialog.name}</strong></p>
                
                {/* Exit Type Selection */}
                <div>
                  <Label className="text-xs mb-2 block">Exit Type</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { value: 'resigned', label: 'Resignation', desc: 'Employee chose to leave', color: 'bg-warning/10 border-warning/30 text-warning' },
                      { value: 'terminated', label: 'Termination', desc: 'Company decision', color: 'bg-error/10 border-error/30 text-error' },
                      { value: 'end_of_contract', label: 'End of Contract', desc: 'Contract expired', color: 'bg-info/10 border-info/30 text-info' },
                    ].map(opt => (
                      <button key={opt.value} type="button"
                        className={`p-2 rounded-xl border text-center transition-all ${resignForm.status === opt.value ? opt.color + ' ring-2 ring-offset-1' : 'bg-white border-border dark:bg-muted dark:border-stone-700'}`}
                        onClick={() => setResignForm({ ...resignForm, status: opt.value, exit_type: opt.value })}
                        data-testid={`exit-type-${opt.value}`}
                      >
                        <p className="text-xs font-medium">{opt.label}</p>
                        <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">{resignForm.status === 'terminated' ? 'Termination Date' : resignForm.status === 'end_of_contract' ? 'Contract End Date' : 'Resignation Date'}</Label>
                    <Input type="date" value={resignForm.resignation_date} onChange={(e) => setResignForm({ ...resignForm, resignation_date: e.target.value })} className="h-9" />
                  </div>
                  <div><Label className="text-xs">Notice Period (days)</Label>
                    <Input type="number" value={resignForm.notice_period_days} onChange={(e) => setResignForm({ ...resignForm, notice_period_days: parseInt(e.target.value) || 0 })} className="h-9" />
                    {resignForm.status === 'terminated' && <p className="text-[10px] text-muted-foreground mt-0.5">Set to 0 for immediate termination</p>}
                  </div>
                </div>
                <div><Label className="text-xs">Reason</Label>
                  <Input value={resignForm.reason} onChange={(e) => setResignForm({ ...resignForm, reason: e.target.value })} 
                    placeholder={resignForm.status === 'terminated' ? 'e.g. Performance issues, Misconduct...' : resignForm.status === 'end_of_contract' ? 'Contract period completed' : 'e.g. Personal reasons, Better opportunity...'}
                    className="h-9" />
                </div>

                {/* EOS Info */}
                <div className={`p-3 rounded-lg text-xs ${resignForm.status === 'terminated' ? 'bg-error/10 dark:bg-red-900/10 text-error dark:text-red-400 border border-error/30' : resignForm.status === 'end_of_contract' ? 'bg-info/10 dark:bg-blue-900/10 text-info dark:text-blue-400 border border-info/30' : 'bg-warning/10 dark:bg-amber-900/10 text-warning dark:text-amber-400 border border-warning/30'}`}>
                  {resignForm.status === 'terminated' ? (
                    <p>Termination: Employee receives full EOS benefits (1/2 salary/year for first 5 years, full salary/year after). Saudi Labor Law.</p>
                  ) : resignForm.status === 'end_of_contract' ? (
                    <p>End of Contract: Full EOS benefits apply (same as termination calculation). Saudi Labor Law.</p>
                  ) : (
                    <p>Resignation: EOS is 0 if under 2 years, 1/3 salary/year for 2-5 years, increasing after. Saudi Labor Law.</p>
                  )}
                </div>

                <Button className="w-full rounded-xl" data-testid="confirm-resign-btn" onClick={async () => {
                  try {
                    await api.post(`/employees/${resignDialog.id}/resign`, { ...resignForm, exit_type: resignForm.status });
                    toast.success(`${resignDialog.name} marked as ${(resignForm.status || '').replace('_', ' ')}`);
                    setResignDialog(null);
                    const r = await api.get('/employees'); setEmployees(r.data);
                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                }}>Confirm Exit</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Email Employee Dialog */}
        <Dialog open={!!emailDialog} onOpenChange={(v) => !v && setEmailDialog(null)}>
          <DialogContent className="max-w-md" data-testid="email-employee-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Mail className="text-info" size={18} /> Email {emailDialog?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div className="text-sm text-muted-foreground bg-info/10 dark:bg-blue-950/30 p-2 rounded-lg border border-info/30">
                To: <span className="font-medium text-foreground">{emailDialog?.email}</span>
              </div>
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="e.g., Schedule Update, Important Notice..." required data-testid="email-subject-input" />
              </div>
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Type your message here..." rows={5} required data-testid="email-message-input" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={sendingEmail} className="rounded-xl bg-info hover:bg-blue-700" data-testid="send-email-btn">
                  {sendingEmail ? 'Sending...' : <><Send size={14} className="mr-1" /> Send Email</>}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEmailDialog(null)} className="rounded-xl">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Settlement Dialog with Clearance */}
        <Dialog open={!!settlementDialog} onOpenChange={(v) => !v && setSettlementDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="settlement-dialog">
            <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><ClipboardCheck size={18} className="text-info" /> Employee Exit & Settlement</DialogTitle></DialogHeader>
            {settlement && settlementDialog && (
              <div className="space-y-4">
                {/* Employee Info */}
                <div className="bg-muted/50 dark:bg-foreground rounded-xl p-3 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Employee</span><span className="font-medium">{settlement.employee_name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="outline" className="capitalize">{settlement.exit_type?.replace('_', ' ') || settlement.status}</Badge></div>
                  <div className="grid grid-cols-2 gap-2">
                    {settlement.join_date && <div className="flex justify-between"><span className="text-muted-foreground">Join Date</span><span>{settlement.join_date}</span></div>}
                    {settlement.end_date && <div className="flex justify-between"><span className="text-muted-foreground">End Date</span><span>{settlement.end_date}</span></div>}
                  </div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Service Period</span><span className="font-medium">{settlement.service_years} years ({settlement.service_days} days)</span></div>
                </div>

                {/* Clearance Checklist */}
                {settlement.clearance && Object.keys(settlement.clearance).length > 0 && (
                  <div className="border rounded-xl p-3 space-y-2" data-testid="clearance-checklist">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold flex items-center gap-1.5">
                        <ClipboardCheck size={14} className="text-warning" /> Clearance Checklist
                      </h4>
                      {settlement.clearance_complete ? (
                        <Badge className="bg-success/20 text-success text-[10px]"><CheckCircle2 size={10} className="mr-1" />All Clear</Badge>
                      ) : (
                        <Badge className="bg-warning/20 text-warning text-[10px]">Pending Items</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {[
                        { key: 'company_assets_returned', label: 'Company Assets Returned' },
                        { key: 'id_card_returned', label: 'ID Card Returned' },
                        { key: 'laptop_returned', label: 'Laptop/Equipment Returned' },
                        { key: 'keys_returned', label: 'Keys Returned' },
                        { key: 'pending_work_handed_over', label: 'Work Handed Over' },
                        { key: 'no_pending_loans', label: 'No Pending Loans' },
                        { key: 'exit_interview_done', label: 'Exit Interview Done' },
                      ].map(item => (
                        <label key={item.key} className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-all ${settlement.clearance[item.key] ? 'bg-success/10 border-success/30 dark:bg-emerald-950/20' : 'bg-white border-border dark:bg-muted dark:border-stone-700'}`} data-testid={`clearance-${item.key}`}>
                          <input
                            type="checkbox"
                            checked={!!settlement.clearance[item.key]}
                            disabled={clearanceUpdating}
                            onChange={async (e) => {
                              setClearanceUpdating(true);
                              try {
                                await api.put(`/employees/${settlementDialog.id}/clearance`, { [item.key]: e.target.checked });
                                setSettlement(prev => ({
                                  ...prev,
                                  clearance: { ...prev.clearance, [item.key]: e.target.checked },
                                  clearance_complete: Object.entries({ ...prev.clearance, [item.key]: e.target.checked }).every(([, v]) => v),
                                }));
                              } catch { toast.error('Failed to update clearance'); }
                              setClearanceUpdating(false);
                            }}
                            className="rounded accent-emerald-600"
                          />
                          <span className={settlement.clearance[item.key] ? 'text-success dark:text-emerald-400 line-through' : ''}>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Settlement Breakdown */}
                <div className="bg-info/10 dark:bg-blue-950/30 rounded-xl p-3 space-y-2 text-sm border border-info/30">
                  <h4 className="font-semibold text-info dark:text-blue-300 text-xs uppercase tracking-wide">Settlement Breakdown</h4>
                  <div className="flex justify-between"><span className="text-muted-foreground">Pending Salary</span><span className="font-mono">{cur()} {settlement.pending_salary?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Leave Encashment ({settlement.leave_balance_days} days)</span><span className="font-mono text-success">+SR {settlement.leave_encashment?.toFixed(2)}</span></div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      End-of-Service Benefit
                      <span className="text-[10px] ml-1 text-info">({settlement.eos_calculation_type === 'termination' ? 'Termination/EOC' : 'Resignation'})</span>
                    </span>
                    <span className="font-mono text-success">+SR {settlement.end_of_service_benefit?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Loan Deduction</span><span className="font-mono text-error">-SR {settlement.loan_balance?.toFixed(2)}</span></div>
                  <hr className="border-info/30" />
                  <div className="flex justify-between text-base font-bold">
                    <span>Total Settlement</span>
                    <span className={settlement.total_settlement >= 0 ? 'text-success' : 'text-error'}>{cur()} {settlement.total_settlement?.toFixed(2)}</span>
                  </div>
                </div>

                {settlement.service_years < 2 && settlement.eos_calculation_type === 'resignation' && (
                  <p className="text-xs text-warning bg-warning/10 p-2 rounded-lg border border-warning/30">
                    Note: End-of-service benefit is SR 0 because service period is less than 2 years (Saudi Labor Law).
                  </p>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-xl" data-testid="download-settlement-pdf" onClick={async () => {
                    try {
                      const response = await api.get(`/employees/${settlementDialog.id}/settlement/pdf`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `settlement_${settlement.employee_name?.replace(/\s+/g, '_')}.pdf`);
                      document.body.appendChild(link); link.click(); link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Settlement PDF downloaded');
                    } catch { toast.error('Failed to download PDF'); }
                  }}><Download size={14} className="mr-1.5" />Download Settlement PDF</Button>
                  <Button className="flex-1 rounded-xl" data-testid="complete-exit-btn" onClick={async () => {
                    try {
                      await api.post(`/employees/${settlementDialog.id}/complete-exit`, { settlement_amount: settlement.total_settlement, paid: true, status: 'left' });
                      toast.success('Employee exit completed');
                      setSettlementDialog(null); setSettlement(null);
                      const r = await api.get('/employees'); setEmployees(r.data);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }}>Complete Exit & Deactivate</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Pay from Bank — unified dialog seeded with this employee. */}
        <BankPaymentDialog
          open={showBankPay}
          onOpenChange={setShowBankPay}
          seedPayee={bankPaySeed}
          onCreated={() => { setShowBankPay(false); setBankPaySeed(null); fetchData(); toast.success('Bank payment recorded'); }}
        />
      </div>
    </DashboardLayout>
  );
}


/* ════════════════════════════════════════════════════════════════════
 * Industry Preset Picker — one-click seeds an industry's standard titles
 * ════════════════════════════════════════════════════════════════════ */
function IndustryPresetPicker({ onSeeded }) {
  const [industries, setIndustries] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await api.get('/job-titles/industries'); setIndustries(r.data || []); }
      finally { setLoading(false); }
    };
    if (open && industries.length === 0) load();
  }, [open, industries.length]);

  useEffect(() => {
    const h = (e) => { if (!e.target.closest?.('[data-industry-picker]')) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const seed = async (industryId, label) => {
    setBusy(industryId);
    try {
      const r = await api.post(`/job-titles/seed-corporate?industry=${industryId}`);
      toast.success(r.data?.message || `${label} titles added`);
      onSeeded?.();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative" data-industry-picker>
      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-xl text-[11px] font-normal text-primary border-primary/30 hover:bg-primary/10"
        data-testid="seed-corporate-titles-btn"
        onClick={() => setOpen(v => !v)}
      >
        <Sparkles size={12} className="mr-1" />Seed Job Titles by Industry
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[360px] rounded-xl border border-border bg-card shadow-xl p-2" data-testid="industry-picker-menu">
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Choose your industry
          </div>
          {loading ? (
            <div className="p-4 text-xs text-center text-muted-foreground">Loading presets…</div>
          ) : (
            <div className="space-y-0.5 max-h-[340px] overflow-y-auto">
              {industries.map((ind) => (
                <button
                  key={ind.id}
                  type="button"
                  onClick={() => seed(ind.id, ind.label)}
                  disabled={busy === ind.id}
                  className="w-full text-left px-2 py-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                  data-testid={`industry-option-${ind.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Briefcase size={13} className="text-primary flex-shrink-0" />
                    <span className="text-xs font-semibold flex-1 truncate">{ind.label}</span>
                    <Badge variant="outline" className="text-[9px]">{ind.title_count} titles</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5 pl-[22px] line-clamp-2">{ind.description}</p>
                </button>
              ))}
            </div>
          )}
          <div className="mt-1 pt-1.5 border-t border-border px-2 py-1 text-[10px] text-muted-foreground">
            Generic corporate titles (CEO · Directors · Managers) are always seeded alongside the industry selection.
          </div>
        </div>
      )}
    </div>
  );
}
